/**
 * `SupabaseRepository` — the hosted implementation of `Repository`
 * (docs/ARCHITECTURE.md §5.1), against whichever Supabase project
 * `VITE_SUPABASE_URL` names for the bundle being built. No project ref is
 * written here: `sourcemap: true` publishes this comment verbatim in the
 * `.map` files served alongside every bundle.
 *
 * The rules this file exists to honour, each one enforced by RLS on the other
 * side (supabase/README.md §4), so getting one wrong reads as "permission
 * denied" rather than as a bug:
 *
 *  - **Insert-time attribution.** The client sets the actor column itself
 *    (`added_by`, `user_id`, `author_id`, `created_by`) *and* `crew_id` on every
 *    crew table. The policies check both.
 *  - **Absence is the "no call" state.** Clearing a vote is a DELETE. `''` is
 *    never written (ARCHITECTURE §1.5).
 *  - **`must_reset` members are read-only.** `is_active()` gates every crew
 *    write; the only way out is `auth.updateUser({password})` followed by
 *    `rpc('complete_password_reset')`.
 *  - **Ordering goes through RPCs** (`add_goty_potential`, `place_tier`,
 *    `nudge_tier`, `set_interest_order`) so two tabs cannot diverge on a
 *    fractional position.
 *  - **Playtest visibility** always excludes `playtest_mutes` rows.
 *  - **Toasts come from `crew_events`**, never from the data tables.
 *
 * Realtime is §2: two channels, patch-by-default, snapshot refetch on every
 * `SUBSCRIBED` (including reconnects), and `crew_events` for attribution.
 *
 * DEVIATIONS are catalogued as P4-xx in docs/PORTING-NOTES.md.
 */

import { ptFmt, ptPartyLbl } from '../lib/format.ts';
import type { RecoveryLink } from '../lib/joinLink.ts';
import { parseRound } from '../lib/round.ts';
import type { RoundSnapshot } from '../lib/round.ts';
import { parseSchedMap, parseSchedNight } from '../lib/schedule.ts';
import type { SchedMap, SchedNom } from '../lib/schedule.ts';
import {
  CHART_DAYS,
  PRICE_CHART_DAYS,
  GOTY_YEAR,
  ptCheckFound,
  ptCheckUrl,
  ptTrackFields,
  PT_BROWSE,
  PT_CHECK_UNAVAILABLE,
  sanitizeVotes,
  TIERS,
} from '../lib/logic.ts';
import type { CcuSample, PriceSample } from '../lib/logic.ts';
import type { DetailFacts,
  MemberGotyYear,
} from './Repository.ts';

/**
 * The shape `SupabaseRepository.loose` exposes: enough of PostgREST's builder
 * to issue the reads whose tables or columns post-date the generated types.
 *
 * Modelled as an OBJECT WITH METHODS rather than as standalone function types,
 * on purpose — a `(t: string) => …` alias is exactly what invites
 * `const from = this.sb.from`, which compiles and then throws at run time
 * because the receiver is gone.
 */
interface LooseFilter {
  eq: (col: string, val: number | string) => LooseFilter;
  gte: (col: string, val: number | string) => LooseFilter;
  in: (col: string, vals: readonly (number | string)[]) => LooseFilter;
  maybeSingle: () => Promise<{ data: Record<string, unknown> | null; error: unknown }>;
  then: <T>(
    onfulfilled: (v: { data: Array<Record<string, unknown>> | null; error: unknown }) => T,
  ) => Promise<T>;
}

interface LooseClient {
  from: (table: string) => { select: (cols: string) => LooseFilter };
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: unknown }>;
}
import type { VideoDraft } from '../lib/videoUrl.ts';
import type {
  AssetMap,
  AssetRec,
  AuthSession,
  BacklogEntry,
  Comment,
  CrewEvent,
  CrewScoreRow,
  BoardEvent,
  SendRec,
  SendStatus,
  WatchRec,
  EntryId,
  Game,
  GameId,
  GameVideo,
  Invite,
  Playtest,
  PlaytestStatus,
  Profile,
  PtBrowseRow,
  PtCheckData,
  PtCheckResult,
  PtOffInput,
  PtSteamInput,
  Role,
  RosterEntry,
  SteamFactPatch,
  SteamLink,
  SyncResult,
  Tier,
  Unsub,
  User,
  VoteValue,
} from '../lib/types.ts';
import type { Database, Json } from '../types/database.types.ts';
import type {
  AssetsResult,
  BrowsePageResult,
  BrowseQuery,
  SeatCount,
  CrewHandlers,
  CrewPatch,
  CrewLinkRow,
  CrewOwnershipRow,
  CrewSnapshot,
  PlayerOverride,
  ResolvedPlayers,
  DiscoverPageResult,
  DiscoverQuery,
  DiscoverReason,
  EnrichResult,
  GotyCandidate,
  GotyLibraryGame,
  GotySignal,
  GotySignalCounts,
  LiveSearchResult,
  MyBoards,
  PersonalHandlers,
  ProvisionalGame,
  Repository,
  SteamBackfill,
  SteamBackfillStatus,
  SteamFillGame,
  SteamFillResult,
  SteamTag,
  SteamWishlistResult,
  WishlistGame,
  CreateCrewInput,
  CrewMembership,
  InviteAccepted,
  InvitePeek,
  InviteSent,
  JoinAsked,
  JoinCodeSent,
  JoinLinkMinted,
  JoinLinkPeek,
  JoinLinkState,
  JoinRequest,
  JoinStatus,
  PendingInvite,
} from './Repository.ts';
import { RepoError, SET_PLAYER_COUNT_ERRORS } from './Repository.ts';

/** One row of migration 3008's reader, until the generated types catch up. */
interface PlayerOverrideRow {
  ovr_min: number | null;
  ovr_max: number | null;
  ovr_at: string | null;
  by_key: string | null;
  by_known: boolean | null;
}
import {
  browseParams,
  factPatchToRow,
  mapAppDetails,
  mapAppMedia,
  normName,
  steamApi,
  toBrowseGame,
  toLiveGame,
} from './steamApi.ts';
import { createQdClient, loginEmail, supabaseEnv } from './supabaseClient.ts';
import type { QdClient, SupabaseEnv } from './supabaseClient.ts';

type Row<T extends keyof Database['public']['Tables']> =
  Database['public']['Tables'][T]['Row'];

/**
 * A round RPC's refusal, by SQLSTATE. `42501` is every rule refusal the
 * functions in 3039 raise (leader only, quota, shut rail…) and `P0002` is "no
 * open round" — neither is a network fault, so neither may be `INTERNAL`,
 * which `mutate.ts` would retry.
 */
function roundErrCode(sqlstate: string | null | undefined): string {
  if (sqlstate === '42501') return 'FORBIDDEN';
  if (sqlstate === 'P0002') return 'NOT_FOUND';
  return 'INTERNAL';
}
type ProfileRow = Row<'profiles'>;
type MembershipRow = Row<'crew_members'>;
/** `public.my_crews()` (3015) — hand-declared alongside the generated types. */
type MyCrewsRow = Database['public']['Functions']['my_crews']['Returns'][number];
type PlaytestRow = Row<'playtests'>;
type VGameRow = Database['public']['Views']['v_games']['Row'];

/**
 * `crew_games_ownership` appids per call. HALF the server's cap (1000, and it
 * raises rather than truncating) so the margin absorbs a caller that asks about
 * the whole catalog as the pool grows, rather than turning that growth into a
 * hard error one day.
 */
const OWNED_BY_CHUNK = 500;

/**
 * `discover_page`'s envelope, as it comes off the wire.
 *
 * Hand-written rather than taken from `Database['public']['Functions']`, because
 * the RPC returns `jsonb` and the generated types can only say `Json` — the
 * shape has to be asserted somewhere, and asserting it here keeps the assertion
 * next to the code that reads each field. Migration 2500's post-condition 5 is
 * the guard on the other side: it fails the migration if `discover_page` stops
 * emitting any column `v_games` exposes, which is what makes the
 * `as unknown as VGameRow` cast below safe rather than hopeful.
 */
interface DiscoverPageRpc {
  rows?: Array<Record<string, unknown> & { owned?: boolean; why?: string; art_lib?: string | null; art_hdr?: string | null }>;
  total?: number;
  hidden_by_filters?: number;
  pool_n?: number;
  tag_counts?: Array<{ id: string; label: string; n: number }>;
  next_cursor?: string | null;
  limit_used?: number;
  personalized?: boolean;
  reason?: string;
  seed?: number;
}

/** An unrecognised `reason` degrades to 'ok' rather than rendering a raw enum. */
const REASONS = new Set<string>(['ok', 'no_link', 'private', 'no_taste', 'empty_table']);

/** Mirrors `_shared/gotyfill.ts#SIGNAL_ORDER`. A signal this build has never
 *  heard of is dropped rather than rendered: the picker's whole job is to say
 *  WHY a game is on the list, and it cannot label what it cannot name. */
const GOTY_SIGNALS = new Set<string>([
  'played', 'achievement', 'ledger', 'recent', 'released',
]);

function isGotySignal(v: unknown): v is GotySignal {
  return typeof v === 'string' && GOTY_SIGNALS.has(v);
}

const BACKFILL_STATUSES = new Set<string>([
  'idle', 'queued', 'running', 'complete', 'failed', 'blocked_private',
]);

/**
 * A member's library-check progress, from EITHER of its two sources: the
 * `backfill` object on `op=goty`'s envelope, which carries `waiting` already
 * worked out, or a raw `steam_backfill_state` row, which carries `completed_at`
 * instead and has it worked out here.
 *
 * The rule is the server's (`_shared/backfill.ts#reportOf`), restated because a
 * Deno function and this bundle cannot share a module: WAIT ONLY FOR A FIRST
 * PASS THAT IS ACTUALLY UNDER WAY. Not for a failed or blocked one, which will
 * never deliver; not for a later top-up run, because the first pass already
 * answered. An unknown status reads as `idle` — never as something to wait on.
 */
export function backfillOf(raw: Record<string, unknown>): SteamBackfill {
  const n = (v: unknown): number => Math.max(0, Math.trunc(Number(v ?? 0)) || 0);
  const status = (
    typeof raw['status'] === 'string' && BACKFILL_STATUSES.has(raw['status']) ? raw['status'] : 'idle'
  ) as SteamBackfillStatus;
  const live = status === 'queued' || status === 'running';
  const waiting = typeof raw['waiting'] === 'boolean'
    ? raw['waiting'] && live
    : live && (raw['completed_at'] ?? null) === null;
  return { status, done: n(raw['done']), total: n(raw['total']), waiting };
}

/** Shared with the store so the year we WRITE and the year we ASK STEAM about
 *  cannot drift apart (`lib/logic.ts#GOTY_YEAR`). */
const DEMO_YEAR = GOTY_YEAR;
/** How many `crew_events` the boot snapshot carries (§2.1 ring buffer). */
const EVENT_LIMIT = 50;
/** ARCHITECTURE §1.10 / D-10: the client-side crop is the normalizer. */
export const AVATAR_PX = 256;

export interface SupabaseRepositoryOptions {
  client?: QdClient;
  env?: Partial<SupabaseEnv>;
  /** Base for `/api/steam/*`. Empty string = same origin (the Vite proxy). */
  apiBase?: string;
  fetchImpl?: typeof fetch;
  now?: () => number;
  /** §2.1: the `me:<uid>` channel. Default on; cheap, and prevents phantom order. */
  personalRealtime?: boolean;
}

// ---------------------------------------------------------------------------

export class SupabaseRepository implements Repository {
  private readonly sb: QdClient;
  private readonly env: SupabaseEnv;
  private readonly apiBase: string;
  private readonly fetchImpl: typeof fetch | null;
  private readonly now: () => number;
  private readonly personalRealtime: boolean;

  /** The signed-in member. Every write derives its actor from here, never from an argument. */
  private me: Profile | null = null;
  private crewId = '';

  // Caches that exist so a realtime row can be translated into a domain patch
  // without a round trip. They are refreshed by every snapshot fetch.
  private keyById = new Map<string, string>();
  private idByKey = new Map<string, string>();
  private appidByEntry = new Map<string, number>();
  private entryByAppid = new Map<number, string>();
  /**
   * comment id → entry id. Needed because a realtime DELETE carries ONLY the
   * primary key: with RLS enabled, Supabase Realtime redacts `old_record` down
   * to the replica-identity columns, since it cannot prove the subscriber may
   * read the rest (verified live — see PORTING-NOTES P4-06). `REPLICA IDENTITY
   * FULL` is still load-bearing, because it is what lets the server evaluate
   * the policy at all and therefore deliver the event; it just does not put the
   * old columns on the wire. Tables whose PK already contains everything the
   * patch needs (`votes`, `playtest_interest`, `playtest_status`) need no map.
   */
  private entryByComment = new Map<string, string>();
  /** video id → appid, for the same reason `entryByComment` exists. */
  private appidByVideo = new Map<string, number>();
  private catalog = new Map<number, Game>();
  private liveGames = new Map<number, Game>();
  private dbAssets = new Map<number, AssetRec>();
  private pts: Playtest[] = [];
  private boards: MyBoards = emptyBoards();
  /** The GOTY year the last `fetchMyBoards` read — the year every board write goes to. */
  private boardYear: number = DEMO_YEAR;

  private crewChannel: ReturnType<QdClient['channel']> | null = null;
  private personalChannel: ReturnType<QdClient['channel']> | null = null;
  /**
   * Detaches the live crew channel AND silences its `onStatus` first.
   *
   * The ordering is the whole reason this is a stored closure rather than a
   * `removeChannel` call at each site. `removeChannel` fires the channel's close
   * hook, and `RealtimeChannel.subscribe` wires that straight to
   * `callback(CLOSED)` — so tearing a channel down reports `error` to whoever
   * was listening. A consumer that resubscribes on `error` (which `store/boot.ts`
   * now does, because a closed channel is gone for good — see `subscribeCrew`)
   * would then be told its own replacement failed, and retry forever.
   */
  private retireCrew: (() => void) | null = null;

  constructor(opts: SupabaseRepositoryOptions = {}) {
    this.env = { ...supabaseEnv(), ...opts.env };
    this.sb = opts.client ?? createQdClient(this.env);
    this.apiBase = opts.apiBase ?? '';
    this.fetchImpl =
      opts.fetchImpl ?? (typeof fetch === 'function' ? fetch.bind(globalThis) : null);
    this.now = opts.now ?? (() => Date.now());
    this.personalRealtime = opts.personalRealtime ?? true;
  }

  /** Detach both channels. Called on sign-out and by the store's teardown. */
  dispose(): void {
    // Through `retireCrew`, not `removeChannel`: a sign-out must not deliver a
    // parting `error` that reads as "the socket broke, resubscribe".
    this.retireCrew?.();
    if (this.personalChannel) void this.sb.removeChannel(this.personalChannel);
    this.crewChannel = null;
    this.personalChannel = null;
  }

  // == identity =============================================================

  async loginRoster(crewSlug: string): Promise<RosterEntry[]> {
    const { data, error } = await this.sb.rpc('login_roster', {
      p_crew_slug: crewSlug || this.env.crewSlug,
    });
    if (error) throw new RepoError('INTERNAL', error.message);
    return (data ?? []).map((r) => ({
      userKey: r.user_key,
      displayName: r.display_name,
      hue: r.hue,
      col: r.col ?? '',
      avatarUrl: r.avatar_url ?? '',
    }));
  }

  async signIn(userKey: string, password: string): Promise<AuthSession> {
    const { data, error } = await this.sb.auth.signInWithPassword({
      email: loginEmail(userKey, this.env.emailDomain),
      password,
    });
    if (error || !data.user) {
      // Supabase answers "Invalid login credentials" for both a bad password and
      // an unknown address, and the roster grid guarantees the member exists —
      // so the honest code is WRONG_PASSWORD.
      throw new RepoError('WRONG_PASSWORD', 'WRONG PASSWORD');
    }
    const profile = await this.loadProfile(data.user.id);
    return {
      userId: profile.userId,
      userKey: profile.userKey,
      authEmail: data.user.email ?? '',
      mustReset: profile.mustReset,
    };
  }

  /**
   * The `must_reset` exit. `complete_password_reset()` is the ONLY path that can
   * clear the flag: a direct column write is reverted by the profiles guard
   * trigger (supabase/README.md §4).
   */
  async setPassword(next: string): Promise<void> {
    const { error } = await this.sb.auth.updateUser({ password: next });
    if (error) throw new RepoError('WEAK_PASSWORD', error.message);
    const { error: rpcErr } = await this.sb.rpc('complete_password_reset');
    if (rpcErr) throw new RepoError('INTERNAL', rpcErr.message);
    if (this.me) this.me = { ...this.me, mustReset: false };
  }

  async changeEmail(next: string): Promise<void> {
    const email = next.trim().toLowerCase();
    const { data, error } = await this.sb.auth.updateUser({ email });
    if (error) {
      // Supabase reports a collision through a couple of shapes depending on
      // version; both mean the same thing to the person typing.
      const m = error.message.toLowerCase();
      if (m.includes('already') || m.includes('registered') || m.includes('exists')) {
        throw new RepoError('EMAIL_TAKEN', 'That email is already in use');
      }
      throw new RepoError('INTERNAL', error.message);
    }

    // NO ERROR IS NOT THE SAME AS DONE, and treating it as such told Marcus his
    // address had changed when it had not (2026-08-21). GoTrue queues the
    // change and answers 200: `email` is still the old one and `new_email`
    // holds what was asked for, until a link is clicked.
    //
    // WORSE, FOR EVERY ACCOUNT MADE THE OLD WAY: with "secure email change"
    // enabled the confirmation is required at BOTH addresses, and the old one
    // is the synthesized `<user_key>@crew.quarterdown.com` — a domain with no
    // MX, which nobody can ever receive mail at. So the change is not slow, it
    // is unfinishable, and saying "signed in with it next time" was the worst
    // possible answer.
    const user = data?.user as { email?: string | null; new_email?: string | null } | undefined;
    const pending = (user?.new_email ?? '').toLowerCase();
    const live = (user?.email ?? '').toLowerCase();
    if (pending === email && live !== email) {
      throw new RepoError('EMAIL_PENDING', 'Confirmation sent — the address has not changed yet');
    }
  }

  /**
   * FORGOT PASSWORD (docs/EMAIL.md). Never says whether the address exists —
   * see the interface for why that is a contract rather than a nicety.
   *
   * `redirectTo` is this origin's root, which is where the app boots and reads
   * the fragment. It is passed rather than left to the Site URL default for one
   * reason: a reset asked for on STAGING must come back to staging. Without it
   * GoTrue uses the project's Site URL, which is correct for the deployed app
   * and sends a developer on localhost to the wrong environment entirely — the
   * same class of mistake `APP_BASE_URL` made with invitations on 2026-08-21.
   *
   * An origin absent from the project's redirect allow-list is not a failure:
   * GoTrue quietly falls back to the Site URL, so the worst case is the old
   * behaviour rather than a dead link.
   */
  async sendPasswordReset(email: string): Promise<void> {
    const addr = email.trim().toLowerCase();
    const origin = typeof window === 'undefined' ? '' : window.location.origin;
    const { error } = await this.sb.auth.resetPasswordForEmail(
      addr,
      origin ? { redirectTo: origin + '/' } : {},
    );
    // GoTrue answers 200 for an address it has never seen, so anything here is
    // a transport or rate-limit problem — about the request, not the account.
    if (error) {
      const m = error.message.toLowerCase();
      if (m.includes('rate') || m.includes('too many') || error.status === 429) {
        throw new RepoError('RATE_LIMITED', 'Too many requests — wait a minute and try again');
      }
      throw new RepoError('INTERNAL', error.message);
    }
  }

  /**
   * Install the session a recovery link carries.
   *
   * `setSession` is what makes the rest of the app work unchanged: it writes
   * the same `localStorage` entry `signInWithPassword` does, so `setPassword`,
   * `complete_password_reset()` and every subsequent read see an ordinary
   * signed-in member.
   *
   * The profile read is not ceremony. A token can be valid for an auth user
   * whose profile row is gone — an account whose crew was deleted underneath
   * it — and letting that through would put somebody on a set-a-password screen
   * that could never finish.
   */
  async resumeRecovery(link: RecoveryLink): Promise<AuthSession> {
    const { data, error } = await this.sb.auth.setSession({
      access_token: link.accessToken,
      refresh_token: link.refreshToken,
    });
    if (error || !data.session?.user) {
      throw new RepoError('LINK_EXPIRED', 'That reset link has expired');
    }
    try {
      const p = await this.loadProfile(data.session.user.id);
      return {
        userId: p.userId,
        userKey: p.userKey,
        authEmail: data.session.user.email ?? '',
        mustReset: p.mustReset,
      };
    } catch {
      throw new RepoError('LINK_EXPIRED', 'That reset link has expired');
    }
  }

  /**
   * Confirm the current password, for the two controls that must not act on a
   * merely-open session: changing the password, and changing the login email.
   *
   * READS THE LIVE AUTH USER rather than deriving `<user_key>@<domain>`.
   * Deriving it was correct only while every account was an invite. It is wrong
   * for two populations that exist now: anybody the wizard created signs in
   * with a REAL address, so the derived one is not their account and reauth
   * failed for them every time — meaning they could never change their own
   * password. And after any successful email change it would keep offering the
   * address that was just replaced, breaking reauth permanently for that
   * account.
   *
   * The fallback keeps the invited path working if the user read fails: those
   * accounts genuinely are `<user_key>@<domain>`.
   */
  async reauth(current: string): Promise<boolean> {
    const key = this.me?.userKey;
    if (!key) return false;
    const { data } = await this.sb.auth.getUser();
    const email = data.user?.email || loginEmail(key, this.env.emailDomain);
    const { error } = await this.sb.auth.signInWithPassword({ email, password: current });
    return !error;
  }

  async signOut(): Promise<void> {
    this.dispose();
    await this.sb.auth.signOut();
    this.me = null;
    this.crewId = '';
    this.clearCaches();
  }

  async currentSession(): Promise<AuthSession | null> {
    const { data, error } = await this.sb.auth.getSession();
    if (error || !data.session) return null;
    try {
      const p = await this.loadProfile(data.session.user.id);
      return {
        userId: p.userId,
        userKey: p.userKey,
        authEmail: data.session.user.email ?? '',
        mustReset: p.mustReset,
      };
    } catch {
      // Authenticated but with no profile row: not a usable member.
      return null;
    }
  }

  onAuthChange(cb: (s: AuthSession | null) => void): Unsub {
    const { data } = this.sb.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        this.dispose();
        this.me = null;
        cb(null);
        return;
      }
      if (event !== 'SIGNED_IN' && event !== 'USER_UPDATED') return;
      void this.loadProfile(session.user.id)
        .then((p) =>
          cb({
            userId: p.userId,
            userKey: p.userKey,
            authEmail: session.user.email ?? '',
            mustReset: p.mustReset,
          }),
        )
        .catch(() => cb(null));
    });
    return () => data.subscription.unsubscribe();
  }

  // == boot =================================================================

  async fetchProfile(userId: string): Promise<Profile> {
    return this.loadProfile(userId);
  }

  private async loadProfile(userId: string): Promise<Profile> {
    const { data, error } = await this.sb
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
    if (error) throw new RepoError('INTERNAL', error.message);
    if (!data) throw new RepoError('NOT_FOUND', 'No profile for this account');
    // `crew_id` is nullable since 3015 and empty for a removed member, so the
    // pointer follows `my_crew_id()`'s own preference order: the active crew
    // first, the legacy home crew only as a fallback. `fetchCrewSnapshot`
    // corrects it afterwards from the crew the server actually served.
    this.crewId = data.active_crew_id ?? data.crew_id ?? '';
    const p: Profile = {
      userId: data.id,
      userKey: data.user_key,
      displayName: data.display_name,
      role: data.role === 'admin' ? 'admin' : 'user',
      hue: data.hue,
      recoveryEmail: data.recovery_email ?? '',
      mustReset: data.must_reset,
      avatarUrl: this.avatarUrl(data),
      lastReviewedAt: Date.parse(data.last_reviewed_at),
    };
    this.me = p;
    this.keyById.set(p.userId, p.userKey);
    this.idByKey.set(p.userKey, p.userId);
    return p;
  }

  /**
   * §5.2 step 3. Ten parallel selects; RLS scopes every one of them to the
   * caller's crew, so `crewId` is informational — the database decides.
   *
   * DEVIATION (P4-03): the store passes its `crewSlug` here (it has no crew
   * uuid before the profile loads). The argument is accepted and ignored.
   */
  async fetchCrewSnapshot(_crewId: string): Promise<CrewSnapshot> {
    const me = this.me;
    if (!me) return emptySnapshot();

    const [
      crew,
      memberships,
      members,
      entries,
      votes,
      comments,
      playtests,
      ptInt,
      ptSt,
      ptMutes,
      seen,
      events,
      videos,
      loginEmails,
    ] = await Promise.all([
      // `crews_select` is `id = my_crew_id()`, so this returns exactly the crew
      // every other read below was scoped to — which is what makes it evidence
      // rather than a guess (D2). maybeSingle: an account with no crew at all
      // legitimately gets no row.
      this.sb.from('crews').select('id, name, slug, created_at').maybeSingle(),
      // The roster is memberships now, not profiles (D9). hue/col/role come
      // from HERE, because they are per-crew: the same person can be a
      // different colour, and a different role, in two crews.
      this.sb
        .from('crew_members')
        .select('crew_id, user_id, role, hue, col, joined_at, added_by'),
      this.sb.from('profiles').select('*').order('created_at'),
      this.sb.from('list_entries').select('*').order('added_at'),
      this.sb.from('votes').select('*'),
      this.sb.from('comments').select('*').is('deleted_at', null).order('created_at'),
      this.sb.from('playtests').select('*').order('created_at'),
      this.sb.from('playtest_interest').select('*'),
      this.sb.from('playtest_status').select('*'),
      this.sb.from('playtest_mutes').select('playtest_id'),
      this.sb.from('whats_new_seen').select('entry_id'),
      this.sb
        .from('crew_events')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(EVENT_LIMIT),
      this.sb.from('game_videos').select('*').order('created_at'),
      // The LOGIN addresses (3029). An RPC rather than a column because
      // `auth.users` is not readable under RLS — see the migration's header for
      // why the roster showing `profiles.recovery_email` was wrong four
      // different ways in one day.
      this.sb.rpc('crew_login_emails'),
    ]);

    const firstError =
      crew.error ??
      memberships.error ??
      members.error ??
      entries.error ??
      votes.error ??
      comments.error ??
      playtests.error ??
      ptInt.error ??
      ptSt.error ??
      ptMutes.error ??
      seen.error ??
      events.error ??
      videos.error ??
      // Included deliberately: a partial roster must never render. If this read
      // alone were tolerated, every member would come back with `loginEmail:
      // ''` and MANAGE CREW would report the whole crew as unable to recover a
      // password — a confident lie is worse than a failed screen.
      loginEmails.error;
    if (firstError) throw new RepoError('INTERNAL', firstError.message);

    // -- roster -------------------------------------------------------------
    //
    // Membership is what makes somebody a member of THIS crew (D9), and the
    // `profiles` read is deliberately not filtered: RLS already admits exactly
    // self + this crew's members + the legacy arm. Joining on the membership
    // map drops the legacy arm's stragglers — an account created in the
    // phase-A-to-B window, or one whose removal predates 3024 — so the roster
    // is memberships even while the policy is still more generous.
    //
    // `me` is kept regardless: you are always on your own roster, and during
    // the no-crew state you are the only row there is.
    const byUser = new Map(
      (memberships.data ?? []).map((m) => [m.user_id as string, m]),
    );

    /**
     * Filter the roster by membership only when there IS a membership set.
     *
     * Zero membership rows beside a non-empty `profiles` read is not a crew
     * with nobody in it — that cannot happen, since you are a member of any
     * crew you can see. It means the membership read came back empty for some
     * other reason, and in that case showing the legacy roster is a far better
     * failure than showing an empty crew: a blank roster loses every
     * attribution on the board, because `keyById` is what maps a vote or a
     * comment back to a name.
     *
     * This is a safety net, not the path. After 3024 a removed member's
     * `crew_id` is cleared, so `profiles_select` stops returning them anyway
     * and the filter has nothing left to catch.
     */
    const scopeToMembers = byUser.size > 0;
    const inCrew = (id: string): boolean => !scopeToMembers || byUser.has(id) || id === me.userId;

    this.crewId = (crew.data?.id as string | undefined) ?? this.crewId;

    this.keyById.clear();
    this.idByKey.clear();
    for (const p of members.data ?? []) {
      if (!inCrew(p.id)) continue;
      this.keyById.set(p.id, p.user_key);
      this.idByKey.set(p.user_key, p.id);
    }

    // -- the shared list ----------------------------------------------------
    this.appidByEntry.clear();
    this.entryByAppid.clear();
    const byEntryId = new Map<string, ReturnType<typeof this.blankEntry>>();
    for (const e of entries.data ?? []) {
      const row = this.blankEntry(e.id, e.game_appid, e.added_by, e.added_at);
      byEntryId.set(e.id, row);
      this.appidByEntry.set(e.id, e.game_appid);
      this.entryByAppid.set(e.game_appid, e.id);
    }
    // Votes are keyed by GAME, not by entry (migration 3004), which is what
    // lets a call outlive the list entry it was cast on. Two consequences here:
    // the appid has to be mapped back to whatever entry uuid the game holds
    // right now, and the select returns rows for games that are NOT on the list
    // — every call remembered from a previous removal. `entryByAppid` misses
    // for those, so the existing `row &&` guard drops them silently, which is
    // exactly right: they are memory, not board state, and they come back the
    // moment the game is re-added.
    for (const v of votes.data ?? []) {
      const entryId = this.entryByAppid.get(v.game_appid);
      const row = entryId === undefined ? undefined : byEntryId.get(entryId);
      const k = this.keyById.get(v.user_id);
      const call = normalizeVote(v.value);
      if (row && k && call) row.votes[k] = call;
    }
    this.entryByComment.clear();
    for (const c of comments.data ?? []) {
      const row = byEntryId.get(c.entry_id);
      this.entryByComment.set(c.id, c.entry_id);
      if (row) row.comments.push(this.toComment(c));
    }
    // The votes bags need no second pass: `normalizeVote` ran the same map on
    // every value as it arrived — the only place the realtime binding can run
    // it too — so this loop is left with the comment ordering.
    for (const row of byEntryId.values()) {
      row.comments.sort((a, b) => a.at - b.at);
    }

    // -- playtests ----------------------------------------------------------
    const muted = new Set((ptMutes.data ?? []).map((m) => m.playtest_id));
    const intBy = new Map<string, Record<string, number>>();
    for (const i of ptInt.data ?? []) {
      const k = this.keyById.get(i.user_id);
      if (!k) continue;
      const bag = intBy.get(i.playtest_id) ?? {};
      bag[k] = Date.parse(i.marked_at);
      intBy.set(i.playtest_id, bag);
    }
    const stBy = new Map<string, Record<string, PlaytestStatus>>();
    for (const s of ptSt.data ?? []) {
      const k = this.keyById.get(s.user_id);
      if (!k) continue;
      const bag = stBy.get(s.playtest_id) ?? {};
      bag[k] = { s: s.status === 'in' ? 'in' : 'req', m: s.note };
      stBy.set(s.playtest_id, bag);
    }
    const pts = (playtests.data ?? [])
      .filter((p) => !muted.has(p.id))
      .map((p) => this.toPlaytest(p, intBy.get(p.id) ?? {}, stBy.get(p.id) ?? {}));
    this.pts = pts;

    // -- what's new ---------------------------------------------------------
    const seenIds: GameId[] = [];
    for (const s of seen.data ?? []) {
      const appid = this.appidByEntry.get(s.entry_id);
      if (appid !== undefined) seenIds.push(appid);
    }

    // Same membership filter as the id maps above, and the membership row is
    // passed in so hue / col / role come from THIS crew rather than from the
    // account-wide legacy columns.
    // No cast: `database.types.ts` was regenerated with 3029, so the shape of
    // this RPC is checked rather than asserted. A cast here would have been the
    // one place a schema change could drift past the compiler unnoticed.
    const loginByUser = new Map(
      (loginEmails.data ?? []).map((r) => [r.user_id, r.login_email ?? '']),
    );
    const users = (members.data ?? [])
      .filter((p) => inCrew(p.id))
      .map((p) =>
        this.toUser(p, p.id === me.userId ? seenIds : [], byUser.get(p.id), loginByUser),
      );

    // -- carousel videos ----------------------------------------------------
    this.appidByVideo.clear();
    const vids = (videos.data ?? []).map((v) => {
      this.appidByVideo.set(v.id, v.game_appid);
      return this.toVideo(v);
    });

    return {
      crew: crew.data
        ? {
            id: crew.data.id as string,
            name: crew.data.name as string,
            slug: crew.data.slug as string,
            createdAt: Date.parse(crew.data.created_at as string),
          }
        : null,
      members: users,
      entries: [...byEntryId.values()],
      playtests: pts,
      seen: seenIds,
      lastReviewedAt: me.lastReviewedAt,
      events: (events.data ?? []).map((e) => this.toEvent(e)).reverse(),
      videos: vids,
    };
  }

  async fetchMyBoards(userId: string, year: number = this.boardYear): Promise<MyBoards> {
    // The year read here is the year every later board write goes to (AA
    // phase 5): the store fetches before it writes.
    this.boardYear = year;
    // Both sampled BEFORE the read; see the cache install at the bottom.
    const quiet = this.boardWritesOpen === 0;
    const epoch = this.boardWriteEpoch;
    // Both boards are per-crew since the 3016 rekey. RLS is what enforces that
    // — the crew predicate lives in both policies' USING — so this filter is
    // the client saying which crew it MEANT, not the thing keeping the crews
    // apart. It is guarded because `''` is not a uuid: an account between
    // crews would turn a scoped read into a 22P02 rather than an empty board.
    const crew = this.crewId;
    const gotyQ = this.sb
      .from('goty_entries')
      .select('*')
      .eq('user_id', userId)
      .eq('year', year);
    const orderQ = this.sb.from('interest_order').select('*').eq('user_id', userId);
    const [goty, order] = await Promise.all([
      crew ? gotyQ.eq('crew_id', crew) : gotyQ,
      crew ? orderQ.eq('crew_id', crew) : orderQ,
    ]);
    if (goty.error) throw new RepoError('INTERNAL', goty.error.message);
    if (order.error) throw new RepoError('INTERNAL', order.error.message);

    const rows = goty.data ?? [];
    const potentials = [...rows]
      .sort((a, b) => a.pot_pos - b.pot_pos)
      .map((r) => r.game_appid as GameId);

    // `goty_entries.tier` is `char(1)` with a CHECK, not an enum, so the column
    // arrives as `string | null`. Narrow it against `TIERS` rather than a literal
    // list — a row written by a newer client with a tier this build does not know
    // is dropped from the board instead of corrupting `tiers`.
    const tiers: Record<string, Tier> = {};
    for (const r of rows) {
      const t = asTier(r.tier);
      if (t !== null) tiers[String(r.game_appid)] = t;
    }

    // The flat S→A→B→C→D→E→F→X concatenation the prototype's `tierOrder` is (§1.6).
    const rank: Record<string, number> = Object.fromEntries(TIERS.map((t, i) => [t, i]));
    const tierOrder = rows
      .filter((r) => r.tier !== null && rank[r.tier] !== undefined)
      .sort(
        (a, b) =>
          (rank[a.tier ?? ''] ?? TIERS.length) - (rank[b.tier ?? ''] ?? TIERS.length) ||
          (a.tier_pos ?? 0) - (b.tier_pos ?? 0),
      )
      .map((r) => r.game_appid as GameId);

    const skips = rows
      .filter((r) => r.skip_pos !== null)
      .sort((a, b) => (a.skip_pos ?? 0) - (b.skip_pos ?? 0))
      .map((r) => r.game_appid as GameId);

    const intOrder = (order.data ?? [])
      .slice()
      .sort((a, b) => a.position - b.position)
      .map((r) => r.game_appid as GameId);

    // `interest_order.skip_pos` (3037) reads by exactly the rule `skips` uses
    // one table over: null is "not skipped", ascending is the deferral order.
    // Rows written before 3037 are all null, so an untouched board arrives
    // with an empty skip queue rather than a fabricated one.
    const intSkips = (order.data ?? [])
      .filter((r) => r.skip_pos !== null && r.skip_pos !== undefined)
      .sort((a, b) => (a.skip_pos ?? 0) - (b.skip_pos ?? 0))
      .map((r) => r.game_appid as GameId);

    const boards: MyBoards = { potentials, tiers, tierOrder, skips, intOrder, intSkips };
    // Only when this read OVERLAPPED no write at all — neither one already
    // open when it started (`quiet`) nor one enqueued while it waited (the
    // epoch, which moves on enqueue rather than on completion so a write that
    // opened AND closed inside the read is still caught). Either way these rows
    // predate the write, and adopting them as the baseline would tell the NEXT
    // write that its predecessor never happened: the stale `prev` that
    // `serializeBoardWrite` exists to prevent, arriving by the other door.
    //
    // The CALLER still gets exactly what the server said. It is only the diff
    // baseline that has to stay honest.
    if (quiet && this.boardWriteEpoch === epoch) {
      this.boards = cloneBoards(boards);
    }
    return boards;
  }

  /**
   * `games_bootstrap` rather than `from('v_games').select('*')`.
   *
   * The old call had no bound, and PostgREST's Max Rows does not error when it
   * is reached — it returns the 1000 LOWEST APPIDS. A GOTY entry above that cut
   * would resolve to null in `gById` and open a blank screen, silently, and only
   * for the members whose boards reach high appids. The pool jobs took
   * production from 24 rows to 490 in one night, so this stopped being a
   * theoretical bound (DISCOVERY.md decision 3).
   *
   * The RPC returns exactly what the client cannot render without — the crew's
   * list, my GOTY board, my interest order, trailers, tracked playtests — plus a
   * bounded popularity head so the Phase 1 fallback still ranks a real universe
   * rather than an arbitrary appid prefix.
   *
   * BOTH cache fills below have to survive the swap. `dbAssets` is tier 1 of
   * §5.3's art ladder and the offline fallback for `giFlush`; `catalog` is what
   * keeps `ensureGameFromCache` from writing `Steam App #<appid>` into the
   * crew-shared table on a 23503 retry.
   */
  async fetchCatalog(appids: number[] = []): Promise<Game[]> {
    const { data, error } = await this.sb.rpc('games_bootstrap', {
      p: { appids: appids.filter((a) => a > 0) },
    });
    if (error) throw new RepoError('INTERNAL', error.message);
    const rows = ((data as { rows?: VGameRow[] } | null)?.rows ?? []) as VGameRow[];
    const games = rows.map((r) => this.toGame(r));
    this.catalog.clear();
    this.dbAssets.clear();
    for (const r of rows) {
      // Tier 1 of §5.3's SWR ladder: the crew-shared art already in steam_facts
      // arrives free with the catalog, and backs `giFlush` when the Worker is
      // unreachable — without ever claiming to be a network round trip.
      if (r.art_lib || r.art_hdr) {
        this.dbAssets.set(r.appid ?? 0, { lib: r.art_lib ?? '', hdr: r.art_hdr ?? '' });
      }
    }
    for (const g of games) this.catalog.set(g.appid, g);
    return games;
  }

  /**
   * The 90-day CCU series for the Detail chart.
   *
   * A plain authenticated SELECT — `20260808_1000_rls.sql` granted it to
   * `authenticated` with an open policy from the start, so no migration was
   * needed to light this up; the table has simply never been read. At two
   * sweeps a day over a 90-day window this is ~180 rows worst case, including
   * the pre-2904 unbucketed stamps that `bucketByDay` collapses on read.
   *
   * Failures return empty rather than throwing: an unavailable chart is a
   * missing chart, and the Detail screen has to render either way. A thrown
   * error here would take the whole detail open down with it.
   */
  async fetchCcuSeries(appid: number): Promise<{ points: CcuSample[]; sample: boolean }> {
    if (!appid) return { points: [], sample: false };
    const since = new Date(Date.now() - CHART_DAYS * 86_400_000).toISOString();
    const { data, error } = await this.sb
      .from('game_ccu_samples')
      .select('sampled_at,ccu')
      .eq('appid', appid)
      .gte('sampled_at', since);
    if (error || !data) return { points: [], sample: false };
    const points: CcuSample[] = [];
    for (const r of data) {
      const at = Date.parse(r.sampled_at as string);
      if (Number.isFinite(at)) points.push({ at, ccu: Number(r.ccu) || 0 });
    }
    return { points, sample: false };
  }

  /**
   * THE CLIENT, loosely typed — for reads whose shape post-dates the checked-in
   * generated `Database` types.
   *
   * IT RETURNS THE CLIENT, NEVER A METHOD, which is the whole reason it exists
   * rather than each call site casting for itself.
   *
   * `PostgrestClient.from` and `.rpc` are PROTOTYPE METHODS that read `this`.
   * Pulling one off the object — `const from = this.sb.from` — drops the
   * receiver, so the call throws `TypeError: Cannot read properties of
   * undefined (reading 'rest')` before any request leaves the process. Verified
   * against @supabase/supabase-js 2.49.4 rather than reasoned about.
   *
   * WHAT IT COST, which is why this is an accessor and not a comment: every
   * such call site failed SILENTLY. `fetchDetailFacts` and `fetchPriceSeries`
   * are fail-soft by contract, so their callers caught the TypeError and cached
   * it as "no answer" — LAST PATCH and REVIEWS · LAST 30D rendered
   * `NEEDS THE … JOB` on a game whose row was fully populated, and the price
   * sparkline showed its empty state, with nothing in any log. The same mistake
   * sat in `fetchCrewSteamLinks` and `fetchPlayerOverride` from the day they
   * shipped, where it cost the FRIENDS roster its linked dots and the player
   * count its attribution line.
   *
   * Deliberately loose: its only job is to preserve the receiver, and each call
   * site still narrows its own result.
   */
  private get loose(): LooseClient {
    return this.sb as unknown as LooseClient;
  }

  /**
   * Migration 3045. `game_price_samples` is granted `select` to `authenticated`
   * with an open policy, so no RPC is needed — the same shape as
   * `fetchCcuSeries` one method up.
   *
   * The window is a DATE column, so the bound is a date literal rather than an
   * ISO timestamp: comparing a `date` to a timestamptz would make the server
   * cast every row, and the samples are UTC-day keyed anyway.
   *
   * Failures answer empty rather than throwing, for the reason the interface
   * gives. `sample: false` on every path — like `fetchCcuSeries`, only
   * `LocalRepository` can claim a fabricated series, so production has no way
   * to render an unlabelled invented chart.
   */
  async fetchPriceSeries(appid: number): Promise<{ points: PriceSample[]; sample: boolean }> {
    if (!appid) return { points: [], sample: false };
    const since = new Date(Date.now() - PRICE_CHART_DAYS * 86_400_000).toISOString().slice(0, 10);
    // Cast for the same narrow reason `fetchPlayerOverride` casts: the TABLE
    // post-dates the checked-in generated types (migration 3045), and the next
    // `generate_typescript_types` makes this redundant rather than wrong.
    // Deliberately not regenerated here — staging is several migrations ahead of
    // production, so a regeneration mid-lane would pull unrelated schema drift
    // into a 2300-line file as a side effect of adding one table.
    const { data, error } = await this.loose.from('game_price_samples')
      .select('sampled_on,price')
      .eq('appid', appid)
      .gte('sampled_on', since);
    if (error || !data) return { points: [], sample: false };
    const points: PriceSample[] = [];
    for (const r of data) {
      // `sampled_on` is a bare `YYYY-MM-DD`; parsing it with an explicit UTC
      // suffix keeps the day the server wrote from sliding a day west in a
      // negative-offset timezone, which is exactly the drift the UTC-day key
      // exists to prevent.
      const at = Date.parse((r.sampled_on as string) + 'T00:00:00Z');
      const price = Number(r.price);
      if (Number.isFinite(at) && Number.isFinite(price)) points.push({ at, price });
    }
    return { points, sample: false };
  }

  /**
   * Migration 3048. One row of `steam_facts`, which is already granted
   * `select` to `authenticated` with an open policy — no RPC, no new grant.
   *
   * `maybeSingle` rather than `single`: an appid with no facts row yet is the
   * normal state for anything the nightly has not reached, and `single` would
   * turn that into an error the caller has to tell apart from a real one.
   */
  async fetchDetailFacts(appid: number): Promise<DetailFacts | null> {
    if (!appid) return null;
    // Cast for the reason `fetchPriceSeries` casts: the columns post-date the
    // checked-in generated types, and the next `generate_typescript_types`
    // makes this redundant rather than wrong.
    const { data, error } = await this.loose.from('steam_facts')
      .select(
        'patch_at,patch_title,patch_seen_at,review_pct_30d,review_score_30d,review_ct_30d,review_30d_at',
      )
      .eq('appid', appid)
      .maybeSingle();
    if (error || !data) return null;
    const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
    const num = (v: unknown): number | null => {
      const n = Number(v);
      return v !== null && v !== undefined && Number.isFinite(n) ? n : null;
    };
    return {
      patchAt: str(data['patch_at']),
      patchTitle: str(data['patch_title']),
      patchSeenAt: str(data['patch_seen_at']),
      rev30Pct: num(data['review_pct_30d']),
      rev30Score: num(data['review_score_30d']),
      rev30Ct: num(data['review_ct_30d']) ?? 0,
      rev30At: str(data['review_30d_at']),
    };
  }

  /**
   * Migration 3007. Failures answer `[]` rather than throwing: an unavailable
   * roster column is a missing column, and the Detail screen renders either
   * way — the same rule `fetchCcuSeries` follows, and for the same reason.
   */
  async fetchCrewOwnership(appid: number): Promise<CrewOwnershipRow[]> {
    if (!appid) return [];
    const { data, error } = await this.sb.rpc('crew_game_ownership', { p_appid: appid });
    if (error || !Array.isArray(data)) return [];
    return data.map((r) => ({
      userKey: r.user_key,
      linked: !!r.linked,
      owned: !!r.owned,
    }));
  }

  /**
   * Migration 3046. Chunked at 500 — half the server's 1000 cap, which raises
   * rather than truncating, so the margin is there to keep a future caller that
   * asks for "the catalog" from tripping it as the pool grows.
   *
   * REJECTS on failure. `fetchCrewOwnership` answers `[]` because it feeds a
   * render path where a missing roster column is survivable; an empty answer
   * here is indistinguishable from "nobody owns anything", and the OWNED BY
   * filter would render that as every board going blank.
   */
  async fetchCrewOwnedBy(appids: readonly number[]): Promise<Record<number, string[]>> {
    const ids = [...new Set(appids.filter((a) => Number.isSafeInteger(a) && a > 0))];
    const out: Record<number, string[]> = {};
    for (let i = 0; i < ids.length; i += OWNED_BY_CHUNK) {
      const chunk = ids.slice(i, i + OWNED_BY_CHUNK);
      // THROUGH `loose`, never `const rpc = this.sb.rpc`. `crew_games_ownership`
      // post-dates the checked-in generated types so it needs the untyped door,
      // and the extraction that door used to invite drops the receiver: `rpc` is
      // a prototype method that reads `this`, so an unbound call throws
      // `TypeError: Cannot read properties of undefined (reading 'rest')` before
      // a request is ever made. It compiles, it type-checks, and `ensureOwnedBy`
      // catches it — so the OWNED BY filter silently narrowed nothing and the
      // DONE count never moved. Exactly the four-call-site defect the `loose`
      // accessor was added to make unreachable; this was a fifth, written the
      // same day against the pre-fix shape.
      const { data, error } = await this.loose.rpc('crew_games_ownership', {
        p_appids: chunk,
      });
      if (error || !Array.isArray(data)) throw new Error('crew_games_ownership failed');
      for (const raw of data as Array<{ appid: number; user_key: string }>) {
        const a = Number(raw.appid);
        if (!Number.isSafeInteger(a) || a <= 0) continue;
        (out[a] ??= []).push(raw.user_key);
      }
    }
    return out;
  }

  /**
   * The BACKLOG's ownership tabs, straight off `steam_owned_apps`.
   *
   * NO `user_id` PREDICATE HERE, and its absence is the point rather than an
   * omission. Migration 1000 puts a self-only select policy on this table
   * (`user_id = auth.uid()`), so the rows this query can reach are already only
   * the caller's; adding the filter client-side would read as though the
   * scoping were ours to get right, and the next person to touch it would
   * believe that. The grant is `select` alone, so nothing here can write.
   *
   * CHUNKED ON THE SAME BOUND as `fetchCrewOwnedBy`. There is no server cap on
   * this one — it is a table read, not the capped RPC — but a `.in()` list
   * becomes a URL, and PostgREST is reached over GET: a shelf of a few hundred
   * appids is a query string long enough to meet a proxy's header limit, which
   * fails as a 414 rather than as anything a caller could read.
   */
  async fetchMyLibrary(appids: readonly number[]): Promise<Record<number, number>> {
    const ids = [...new Set(appids.filter((a) => Number.isSafeInteger(a) && a > 0))];
    const out: Record<number, number> = {};
    for (let i = 0; i < ids.length; i += OWNED_BY_CHUNK) {
      const chunk = ids.slice(i, i + OWNED_BY_CHUNK);
      // Through `loose` for the receiver's sake, exactly as the RPC above — the
      // extraction that drops `this` is the same defect either door.
      const { data, error } = await this.loose
        .from('steam_owned_apps')
        .select('appid, playtime_min')
        .in('appid', chunk);
      if (error || !Array.isArray(data)) throw new Error('steam_owned_apps read failed');
      for (const raw of data as Array<{ appid: number; playtime_min: number }>) {
        const a = Number(raw.appid);
        if (!Number.isSafeInteger(a) || a <= 0) continue;
        // `playtime_min` is `not null default 0`, so a non-numeric value here
        // means a shape change rather than a quiet zero — floor it and move on
        // instead of writing `NaN` into a map the tabs compare against.
        out[a] = Math.max(0, Math.trunc(Number(raw.playtime_min ?? 0)) || 0);
      }
    }
    return out;
  }

  /**
   * Migration 3010. Unlike its sibling above this REJECTS rather than answering
   * `[]` — see the interface doc-comment: a blank roster dot that retries next
   * visit beats a cached wrong one, and nothing here renders on a hot path.
   *
   * Cast for the same narrow reason `fetchPlayerOverride` casts: the function
   * post-dates the checked-in generated types, and the next
   * `generate_typescript_types` makes this redundant rather than wrong.
   */
  async fetchCrewSteamLinks(): Promise<CrewLinkRow[]> {
    const { data, error } = await this.loose.rpc('crew_steam_links');
    if (error || !Array.isArray(data)) throw new Error('crew_steam_links failed');
    return (data as Array<{ user_key: string; linked: boolean }>).map((r) => ({
      userKey: r.user_key,
      linked: !!r.linked,
    }));
  }

  /**
   * Migration 3059. Rejects rather than answering `[]` for the same reason
   * `fetchCrewSteamLinks` above does, and here the distinction is sharper: `[]`
   * is the RPC's honest answer for a member who has ranked nothing, so a
   * swallowed error would be indistinguishable from one — and would then be
   * CACHED as that member's ordering for the rest of the session.
   *
   * Cast for the same narrow reason the two above cast: the function post-dates
   * the checked-in generated types, and the next `generate_typescript_types`
   * makes this redundant rather than wrong. The RPC returns a bare `integer[]`,
   * not a row set, so `data` is the array itself.
   */
  async fetchCrewInterestOrder(userKey: string): Promise<GameId[]> {
    const { data, error } = await this.loose.rpc('crew_interest_order', { p_user_key: userKey });
    if (error) throw new Error('crew_interest_order failed');
    // `null` is not in the function's range — it coalesces — but a deploy that
    // predates 3059 answers with an error above, and anything else non-array is
    // a shape this build does not understand. Neither may become `[]`.
    if (!Array.isArray(data)) throw new Error('crew_interest_order returned a non-array');
    return (data as unknown[]).map(Number).filter((n) => Number.isSafeInteger(n) && n > 0);
  }

  // == my games (AA phase 2; migration 3060) =================================

  async fetchBacklog(): Promise<BacklogEntry[]> {
    const me = this.requireMe();
    // `user_id = me` is the client saying which rows it MEANT; the self-only
    // policy is what keeps everybody else's out.
    const { data, error } = await this.sb
      .from('backlog')
      .select('game_appid, added_at')
      .eq('user_id', me.userId)
      .order('added_at', { ascending: true });
    if (error) throw new RepoError('INTERNAL', error.message);
    return (data ?? []).map((r) => ({ id: r.game_appid as GameId, at: Date.parse(r.added_at) }));
  }

  async addBacklog(appid: GameId): Promise<void> {
    const me = this.requireMe();
    const id = Number(appid);
    if (!Number.isSafeInteger(id) || id <= 0) {
      // D-02, the same gate `quickAdd` has: a CheapShark-only row has no appid
      // the FK can hold.
      throw new RepoError('NO_APPID', "NO STEAM PAGE — CAN'T ADD YET");
    }
    let { error } = await this.sb.from('backlog').insert({ user_id: me.userId, game_appid: id });
    if (error && error.code === '23503') {
      // A live-search row the catalog has never seen: mint it and retry once,
      // exactly as `quickAdd` does.
      await this.ensureGameFromCache(id);
      ({ error } = await this.sb.from('backlog').insert({ user_id: me.userId, game_appid: id }));
    }
    // Already shelved is not a failure — the store guards it, and a second tab
    // racing this one should land on the same answer.
    if (error && error.code !== '23505') throw new RepoError('INTERNAL', error.message);
  }

  async addBacklogMany(appids: GameId[]): Promise<void> {
    const me = this.requireMe();
    const ids = [...new Set(appids.map(Number))].filter((id) => Number.isSafeInteger(id) && id > 0);
    if (!ids.length) return;
    // `ignoreDuplicates` is `ON CONFLICT DO NOTHING`, which needs only the
    // INSERT grant 3060 gives. That is NOT true of a plain upsert: its DO
    // UPDATE SET list is checked against UPDATE on every column it names
    // whether or not it runs, which is how `game_watch` failed (04c203e), and
    // `backlog` grants no UPDATE at all. Verified under the authenticated role
    // on staging, 2026-09-15, in a rolled-back transaction: a second identical
    // statement is a no-op, not a 23505.
    const { error } = await this.sb
      .from('backlog')
      .upsert(
        ids.map((id) => ({ user_id: me.userId, game_appid: id })),
        { onConflict: 'user_id,game_appid', ignoreDuplicates: true },
      );
    if (error) throw new RepoError('INTERNAL', error.message);
  }

  /**
   * `steam-sync op=wishlist` (docs/DISCOVERY.md §7.1a). The function mints
   * nothing; the store does, through `ensureGame`, as the GOTY auto-fill does.
   */
  async steamWishlist(): Promise<SteamWishlistResult> {
    const res = await this.invoke<{
      open?: boolean;
      total?: number;
      games?: Array<{ appid?: number; name?: string; developer?: string | null; upcoming?: boolean }>;
      not_games?: number;
      unresolved?: number;
      unnamed?: number;
    }>('steam-sync', { op: 'wishlist' });
    if (!res.ok) throw new RepoError(res.code, res.message);

    const int = (v: unknown): number => Math.max(0, Math.trunc(Number(v ?? 0)) || 0);
    const games: WishlistGame[] = [];
    for (const g of res.data.games ?? []) {
      const appid = Number(g?.appid);
      if (!Number.isSafeInteger(appid) || appid <= 0) continue;
      games.push({
        appid,
        name: (g?.name ?? '').trim(),
        dev: (g?.developer ?? '').trim(),
        upcoming: g?.upcoming === true,
      });
    }
    return {
      // Only an explicit `true` opens it: a function that predates the op
      // answers `Unknown op` above, and anything else must not read as a list.
      open: res.data.open === true,
      games,
      total: int(res.data.total),
      notGames: int(res.data.not_games),
      unresolved: int(res.data.unresolved),
      unnamed: int(res.data.unnamed),
    };
  }

  async removeBacklog(appid: GameId): Promise<void> {
    const me = this.requireMe();
    const { error } = await this.sb
      .from('backlog')
      .delete()
      .eq('user_id', me.userId)
      .eq('game_appid', Number(appid));
    if (error) throw new RepoError('INTERNAL', error.message);
  }

  async fetchScores(): Promise<CrewScoreRow[]> {
    // No filter: `game_scores_select` already answers "mine plus my active
    // crew's active members", and that is the read the notice board wants.
    // `prev` (3067): the number a re-score replaced, kept by the row's own
    // trigger — MY SCORES prints it as `WAS 6`.
    const { data, error } = await this.sb.from('game_scores').select('user_id, game_appid, score, scored_at, prev');
    if (error) throw new RepoError('INTERNAL', error.message);
    const out: CrewScoreRow[] = [];
    for (const r of data ?? []) {
      // The policy admits only crew members and they are all in the snapshot,
      // so an unknown id is a row from a member the snapshot has not caught up
      // with yet — dropped now, back on the next refetch.
      const k = this.keyById.get(r.user_id);
      if (!k) continue;
      out.push({ userKey: k, id: r.game_appid as GameId, v: r.score, at: Date.parse(r.scored_at), prev: r.prev ?? null });
    }
    return out;
  }

  /**
   * 3062's read. No filter: `recommendations_select` already answers "the
   * rows I am party to in my active crew", and both surfaces (TO YOU, FROM
   * YOU) read from that one set.
   */
  async fetchWatch(): Promise<Record<string, WatchRec>> {
    const me = this.requireMe();
    const { data, error } = await this.sb
      .from('game_watch')
      .select('game_appid, ea, rel_date, price, sale, demo, upd, dlc, cap')
      .eq('user_id', me.userId);
    if (error) throw new RepoError('INTERNAL', error.message);
    const out: Record<string, WatchRec> = {};
    for (const r of data ?? []) {
      out[String(r.game_appid)] = {
        ea: r.ea,
        date: r.rel_date,
        price: r.price,
        sale: r.sale,
        demo: r.demo,
        upd: r.upd,
        dlc: r.dlc,
        cap: r.cap,
      };
    }
    return out;
  }

  async putWatch(appid: GameId, rec: WatchRec | null): Promise<void> {
    const me = this.requireMe();
    const id = Number(appid);
    if (!Number.isSafeInteger(id) || id <= 0) throw new RepoError('NO_APPID', "NO STEAM PAGE — CAN'T WATCH IT YET");
    const armed = rec && (rec.ea || rec.date || rec.price || rec.sale || rec.demo || rec.upd || rec.dlc);
    if (!armed || !rec) {
      // Nothing armed is no watch: the row goes (the table's CHECK would
      // refuse it anyway).
      const { error } = await this.sb.from('game_watch').delete().eq('user_id', me.userId).eq('game_appid', id);
      if (error) throw new RepoError('INTERNAL', error.message);
      return;
    }
    const cols = {
      ea: rec.ea,
      rel_date: rec.date,
      price: rec.price,
      sale: rec.sale,
      demo: rec.demo,
      upd: rec.upd,
      dlc: rec.dlc,
      cap: Math.max(1, Math.round(rec.cap)),
    };
    // UPDATE, then INSERT — never an upsert (Marcus, production, 2026-09-04:
    // "couldn't save try again"; the log said `permission denied for table
    // game_watch`). 3063 grants UPDATE on the trigger columns only, so the
    // sweep alone can write `snapshot` — and Postgres checks that grant
    // against every column in an upsert's SET list, key columns included,
    // whether or not the conflict branch runs. Two plain statements need
    // exactly the grants the table has.
    const upd = await this.sb
      .from('game_watch')
      .update(cols)
      .eq('user_id', me.userId)
      .eq('game_appid', id)
      .select('game_appid');
    if (upd.error) throw new RepoError('INTERNAL', upd.error.message);
    if ((upd.data ?? []).length > 0) return;
    const row = { user_id: me.userId, game_appid: id, ...cols };
    let { error } = await this.sb.from('game_watch').insert(row);
    if (error && error.code === '23503') {
      // A game the catalog has never carried: mint it and retry once, as the
      // shelf does.
      await this.ensureGameFromCache(id);
      ({ error } = await this.sb.from('game_watch').insert(row));
    }
    // A second tab landed the same watch first: theirs stands, not an error.
    if (error && error.code !== '23505') throw new RepoError('INTERNAL', error.message);
  }

  async fetchBoard(): Promise<BoardEvent[]> {
    // No user filter: `board_events_select` answers "mine, plus my active
    // crew's PARTY rows" — the board's exact read. The window is ours.
    const since = new Date(Date.now() - 14 * 864e5).toISOString();
    const { data, error } = await this.sb
      .from('board_events')
      .select('id, at, kind, game_appid, actor_user, payload')
      .gte('at', since)
      .order('at', { ascending: false })
      .limit(400);
    if (error) throw new RepoError('INTERNAL', error.message);
    const out: BoardEvent[] = [];
    for (const r of data ?? []) {
      // A PARTY row from a member the snapshot has not caught up with is
      // dropped now and back on the next read, as a score would be.
      const actor = r.actor_user ? (this.keyById.get(r.actor_user) ?? null) : null;
      if (r.actor_user && !actor) continue;
      const payload = r.payload && typeof r.payload === 'object' && !Array.isArray(r.payload) ? (r.payload as Record<string, unknown>) : {};
      out.push({ id: r.id, at: Date.parse(r.at), kind: r.kind as BoardEvent['kind'], gid: r.game_appid as GameId, actor, payload });
    }
    return out;
  }

  async fetchSends(): Promise<SendRec[]> {
    const { data, error } = await this.sb
      .from('recommendations')
      .select('id, from_user, to_user, game_appid, note, sent_at, status');
    if (error) throw new RepoError('INTERNAL', error.message);
    const out: SendRec[] = [];
    for (const r of data ?? []) {
      // Both parties are crew members and in the snapshot; an unknown id is a
      // member the snapshot has not caught up with — back on the next read.
      const from = this.keyById.get(r.from_user);
      const to = this.keyById.get(r.to_user);
      if (!from || !to) continue;
      const st: SendStatus = r.status === 'added' ? 'added' : r.status === 'x' ? 'x' : '';
      out.push({ id: r.id, from, to, gid: r.game_appid as GameId, note: r.note, at: Date.parse(r.sent_at), st });
    }
    return out;
  }

  async setSends(appid: GameId, to: string[], note: string, edit: boolean): Promise<void> {
    this.requireMe();
    const id = Number(appid);
    if (!Number.isSafeInteger(id) || id <= 0) throw new RepoError('NO_APPID', "NO STEAM PAGE — CAN'T RECOMMEND IT YET");
    const ids: string[] = [];
    for (const k of to) {
      const uid = this.idByKey.get(k);
      if (!uid) throw new RepoError('INTERNAL', 'NOT_A_MEMBER');
      ids.push(uid);
    }
    const args = { p_appid: id, p_to: ids, p_note: note, p_edit: edit };
    let { error } = await this.sb.rpc('recommend_set', args);
    if (error && error.code === '23503') {
      // A game the catalog has never carried (a Steam-search row on detail):
      // mint it and retry once, as `quickAdd` and `addBacklog` do.
      await this.ensureGameFromCache(id);
      ({ error } = await this.sb.rpc('recommend_set', args));
    }
    if (error) throw new RepoError('INTERNAL', error.message);
  }

  async answerSend(id: string, st: SendStatus): Promise<void> {
    this.requireMe();
    const { error } = await this.sb.rpc('recommend_answer', { p_id: id, p_status: st });
    if (error) throw new RepoError('INTERNAL', error.message);
  }

  async setScore(appid: GameId, score: number | null): Promise<void> {
    const me = this.requireMe();
    const id = Number(appid);
    if (!Number.isSafeInteger(id) || id <= 0) throw new RepoError('NO_APPID', "NO STEAM PAGE — CAN'T SCORE IT YET");
    if (score === null) {
      // Cleared is absence (3060's comment on the table), never a zero.
      const { error } = await this.sb.from('game_scores').delete().eq('user_id', me.userId).eq('game_appid', id);
      if (error) throw new RepoError('INTERNAL', error.message);
      return;
    }
    // `scored_at` is written explicitly so a CHANGED score re-dates itself —
    // the notice board keeps 14 days from the last time you said something,
    // and the column default would only ever date the first time.
    const row = { user_id: me.userId, game_appid: id, score, scored_at: new Date().toISOString() };
    let { error } = await this.sb.from('game_scores').upsert(row, { onConflict: 'user_id,game_appid' });
    if (error && error.code === '23503') {
      await this.ensureGameFromCache(id);
      ({ error } = await this.sb.from('game_scores').upsert(row, { onConflict: 'user_id,game_appid' }));
    }
    if (error) throw new RepoError('INTERNAL', error.message);
  }

  async fetchPlayerOverride(appid: number): Promise<PlayerOverride | null> {
    if (!appid) return null;
    // `game_player_override` post-dates the checked-in generated types — it is
    // migration 3008, applied with this release. The cast is narrow on purpose:
    // the next `generate_typescript_types` makes it redundant rather than wrong,
    // and it names the row shape the migration actually returns rather than
    // widening to `any`.
    const { data, error } = await this.loose.rpc('game_player_override', { p_appid: appid });
    if (error || !Array.isArray(data) || !data[0]) return null;
    const r = data[0] as PlayerOverrideRow;
    return {
      min: r.ovr_min ?? 0,
      max: r.ovr_max ?? 0,
      at: r.ovr_at ? Date.parse(r.ovr_at) : 0,
      byKey: r.by_key ?? '',
      byKnown: !!r.by_known,
    };
  }

  async setPlayerCount(
    appid: number,
    min: number | null,
    max: number | null,
  ): Promise<ResolvedPlayers | null> {
    // NULLS ARE THE CONTRACT, not a gap in it: `p_min`/`p_max` both null is how
    // the function expresses a CLEAR — it has no reset flag, and one-sided is
    // `BAD_RANGE` rather than a clear. The generated signature types every
    // parameter as required and non-nullable because PostgREST cannot express
    // "nullable with a default", so the cast restores what the SQL states.
    //
    // `p_note` is always null. The function accepts 200 characters and W5
    // collects none, because it would be the only member-typed free text on a
    // globally-writable row — see migration 3008's header and W5.md.
    const args = { p_appid: appid, p_min: min, p_max: max, p_note: null } as unknown as {
      p_appid: number;
      p_min: number;
      p_max: number;
      p_note: string;
    };
    const { error } = await this.sb.rpc('set_player_count', args);
    if (error) {
      // The function raises bare strings, so the code arrives inside the message
      // rather than in `error.code`. Matched by inclusion so a Postgres prefix
      // (`P0001: ...`) cannot turn a diagnosable refusal into a generic one.
      const m = error.message || '';
      for (const code of SET_PLAYER_COUNT_ERRORS) {
        if (m.includes(code)) throw new Error(code);
      }
      throw new Error('FAILED');
    }
    // Read the resolver's answer rather than predicting it. Required for CLEAR,
    // whose result is whichever rung was underneath — a value this client has
    // never been sent. Failing this read is not a failed write: the write
    // committed, so `null` means "saved, refresh to see it" and never "undo".
    const { data, error: readErr } = await this.sb
      .from('v_games')
      .select('min_players, max_players, players_unbounded, players_src')
      .eq('appid', appid)
      .maybeSingle();
    if (readErr || !data) return null;
    const src = data.players_src;
    return {
      mn: data.min_players ?? 0,
      mx: data.max_players ?? 0,
      punb: data.players_unbounded === true,
      ...(src === 'override' || src === 'editorial' || src === 'igdb' || src === 'pcgw' || src === 'steam_solo'
        ? { psrc: src }
        : null),
    };
  }

  async fetchSteamLink(userId: string): Promise<SteamLink | null> {
    const { data, error } = await this.sb
      .from('steam_links')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    if (error || !data) return null;
    return {
      linked: true,
      profilePublic: data.profile_public,
      personaName: data.persona_name ?? '',
      lastSyncAt: data.last_sync_at ? Date.parse(data.last_sync_at) : null,
      ownedCount: data.owned_count ?? 0,
    };
  }

  // == profile ==============================================================

  async savePf(p: { displayName: string; recoveryEmail: string }): Promise<void> {
    const me = this.requireMe();
    const { error } = await this.sb
      .from('profiles')
      .update({ display_name: p.displayName, recovery_email: p.recoveryEmail || null })
      .eq('id', me.userId);
    if (error) throw new RepoError('INTERNAL', error.message);
    this.me = { ...me, displayName: p.displayName, recoveryEmail: p.recoveryEmail };
  }

  /**
   * D-10: 256² centre-crop → `avatars/<crew_id>/<user_id>.jpg` → `profiles`
   * pointer → public URL plus the `?v=<epoch>` cache-buster, without which a
   * changed avatar appears not to take (public-bucket URLs are CDN-cached hard).
   */
  async onPickAv(dataUrl: string): Promise<{ avatarUrl: string }> {
    const me = this.requireMe();
    if (!this.crewId) throw new RepoError('NOT_SIGNED_IN');
    const blob = await normalizeAvatar(dataUrl);
    const path = this.crewId + '/' + me.userId + '.jpg';

    const up = await this.sb.storage
      .from('avatars')
      .upload(path, blob, { upsert: true, contentType: 'image/jpeg', cacheControl: '3600' });
    if (up.error) throw new RepoError('INTERNAL', up.error.message);

    const stamp = new Date(this.now()).toISOString();
    const { error } = await this.sb
      .from('profiles')
      .update({ avatar_path: path, avatar_updated_at: stamp })
      .eq('id', me.userId);
    if (error) throw new RepoError('INTERNAL', error.message);

    const avatarUrl = this.publicAvatarUrl(path, stamp);
    this.me = { ...me, avatarUrl };
    return { avatarUrl };
  }

  async setColor(hue: number | null, col: string | null): Promise<void> {
    // Omission IS the null: both args default null in SQL (3013), because
    // typegen only marks an argument optional when the SQL declares a
    // DEFAULT — a passed `null` would stop compiling at the next regen. A
    // tile pick omits p_col and the custom color clears; a custom apply
    // omits p_hue and the hue stands.
    const args: { p_hue?: number; p_col?: string } = {};
    if (hue !== null) args.p_hue = hue;
    if (col !== null) args.p_col = col;
    const { error } = await this.sb.rpc('set_profile_color', args);
    if (error) throw new RepoError('INTERNAL', error.message);
  }

  /**
   * Clears the pointer only — the storage object stays. Deliberate: re-picking
   * an image is the common regret path, storage upserts by path anyway, and a
   * delete needs storage RLS this client does not hold.
   */
  async dropAvatar(): Promise<void> {
    const me = this.requireMe();
    const { error } = await this.sb
      .from('profiles')
      .update({ avatar_path: null, avatar_updated_at: null })
      .eq('id', me.userId);
    if (error) throw new RepoError('INTERNAL', error.message);
    this.me = { ...me, avatarUrl: '' };
  }

  // == list / votes / comments ==============================================

  async ensureGame(p: ProvisionalGame): Promise<number> {
    const { data, error } = await this.sb.rpc('ensure_game', {
      p: {
        appid: p.appid,
        name: p.name,
        dev: p.dev,
        release_ym: p.releaseYm,
        price: p.price,
        old_price: p.oldPrice,
        upcoming: p.upcoming,
        description: p.description,
        art_url: p.artUrl,
      },
    });
    if (error) throw new RepoError('INTERNAL', error.message);
    return data ?? p.appid;
  }

  async quickAdd(appid: GameId): Promise<import('../lib/types.ts').ListEntry> {
    const me = this.requireMe();
    const id = Number(appid);
    if (!Number.isSafeInteger(id) || id <= 0) {
      // D-02: a CheapShark-only row has no appid the FK can hold.
      throw new RepoError('NO_APPID', "NO STEAM PAGE — CAN'T ADD YET");
    }

    let row = await this.insertEntry(id, me.userId);
    if (!row.ok && row.code === '23503') {
      // The game is a live-search result the catalog has never seen. Mint the
      // provisional row (ARCHITECTURE §1.12) and retry exactly once.
      await this.ensureGameFromCache(id);
      row = await this.insertEntry(id, me.userId);
    }
    if (!row.ok) {
      if (row.code === '23505') throw new RepoError('DUPLICATE', 'ALREADY ON THE LIST');
      throw new RepoError('INTERNAL', row.message);
    }
    this.appidByEntry.set(row.data.id, id);
    this.entryByAppid.set(id, row.data.id);

    // The game may have been on this list before. Since 3004 the calls from
    // that life are still in the table, keyed by game rather than by the entry
    // that died, so re-adding is also un-forgetting.
    //
    // This second round trip is what makes the RE-ADDER see them. It cannot be
    // left to realtime: the store is still holding an optimistic entry whose id
    // is `pending_<appid>` until this promise resolves (list slice), and a
    // `vote.set` patch naming an entry uuid the store has never seen is dropped
    // on the floor. Other members have no such placeholder and get their copy
    // from the list_entries binding below.
    //
    // A failure here is not a failed add — the entry exists, and the next
    // snapshot refetch folds the calls in. Degrade to the empty bag.
    const entry = this.blankEntry(row.data.id, id, me.userId, row.data.added_at);
    entry.votes = await this.fetchVoteBag(id);
    return entry;
  }

  /**
   * Every call on one game, as the `votes` bag shape the store speaks.
   * `votes_select` is crew-wide (RLS 1000:163-165), so this returns the whole
   * crew's calls, not just the caller's.
   */
  private async fetchVoteBag(appid: number): Promise<Record<string, VoteValue>> {
    const bag: Record<string, VoteValue> = {};
    const { data, error } = await this.sb
      .from('votes')
      .select('user_id, value')
      .eq('game_appid', appid);
    if (error) return bag;
    for (const v of data ?? []) {
      const k = this.keyById.get(v.user_id);
      const call = normalizeVote(v.value);
      if (k && call) bag[k] = call;
    }
    return bag;
  }

  private async insertEntry(
    appid: number,
    userId: string,
  ): Promise<
    | { ok: true; data: { id: string; added_at: string } }
    | { ok: false; code: string; message: string }
  > {
    const { data, error } = await this.sb
      .from('list_entries')
      .insert({ crew_id: this.crewId, game_appid: appid, added_by: userId })
      .select('id, added_at')
      .single();
    if (error) return { ok: false, code: error.code ?? '', message: error.message };
    return { ok: true, data };
  }

  private async ensureGameFromCache(appid: number): Promise<void> {
    const g = this.liveGames.get(appid) ?? this.catalog.get(appid);
    await this.ensureGame({
      appid,
      name: g?.n ?? 'Steam App #' + appid,
      dev: g?.dev ?? '—',
      releaseYm: g?.rd ?? '',
      price: g?.p ?? null,
      oldPrice: g?.o ?? 0,
      upcoming: g?.up ?? false,
      description: g?.ds ?? '',
      artUrl: g?.art ?? '',
    });
  }

  async removeFromList(entryId: EntryId): Promise<void> {
    const { error } = await this.sb.from('list_entries').delete().eq('id', entryId);
    if (error) throw new RepoError('INTERNAL', error.message);
    const appid = this.appidByEntry.get(entryId);
    this.appidByEntry.delete(entryId);
    if (appid !== undefined) this.entryByAppid.delete(appid);
  }

  /**
   * §1.5: clearing a vote DELETEs. `''` is never stored — and since 3004 that
   * rule carries more weight, not less: a cleared call leaves no row, so there
   * is nothing to remember and nothing to bring back. Silence stays silence,
   * `c` stays a verdict.
   *
   * The store speaks entry uuids and the table is keyed by game, so the
   * translation happens here at the seam and nowhere else.
   */
  async setVote(entryId: EntryId, value: VoteValue | null): Promise<void> {
    const me = this.requireMe();
    const appid = this.appidByEntry.get(entryId);
    if (appid === undefined) throw new RepoError('INTERNAL', 'unknown entry');
    if (value === null) {
      const { error } = await this.sb
        .from('votes')
        .delete()
        .eq('crew_id', this.crewId)
        .eq('game_appid', appid)
        .eq('user_id', me.userId);
      if (error) throw new RepoError('INTERNAL', error.message);
      return;
    }
    const { error } = await this.sb
      .from('votes')
      .upsert(
        { game_appid: appid, user_id: me.userId, crew_id: this.crewId, value },
        { onConflict: 'crew_id,game_appid,user_id' },
      );
    if (error) throw new RepoError('INTERNAL', error.message);
  }

  /**
   * P2-03 in reverse: the store hands over a whole order array, the schema wants
   * a fractional position. A single-item move — which is what every real call
   * site (`voteSet`, `setIntAt`, `nudgeInt`) produces — is replayed through the
   * `set_interest_order` RPC so the server owns the arithmetic. Anything else
   * falls back to a bulk rewrite of 1..n, which is idempotent and matches the
   * array exactly.
   *
   * SERIALIZED, because "anything else" is reachable by accident. See
   * `serializeBoardWrite`.
   */
  async setIntAt(order: GameId[]): Promise<void> {
    return this.serializeBoardWrite(() => this.setIntAtNow(order));
  }

  private async setIntAtNow(order: GameId[]): Promise<void> {
    const me = this.requireMe();
    const next = order.map(Number).filter((n) => Number.isSafeInteger(n) && n > 0);
    const prev = this.boards.intOrder.map(Number).filter((n) => Number.isSafeInteger(n) && n > 0);

    const moved = singleMove(prev, next);
    if (moved !== null) {
      const at = next.indexOf(moved);
      const before = at >= 0 && at + 1 < next.length ? next[at + 1] : undefined;
      const args =
        before === undefined
          ? { p_appid: moved }
          : { p_appid: moved, p_before_appid: before };
      const { error } = await this.sb.rpc('set_interest_order', args);
      if (error) throw new RepoError('INTERNAL', error.message);
    } else if (next.length) {
      const rows = next.map((appid, i) => ({
        user_id: me.userId,
        game_appid: appid,
        crew_id: this.crewId,
        position: i + 1,
      }));
      const { error } = await this.sb
        .from('interest_order')
        .upsert(rows, { onConflict: 'crew_id,user_id,game_appid' });
      if (error) throw new RepoError('INTERNAL', error.message);
    }
    this.boards = { ...this.boards, intOrder: [...order] };
  }

  async postC(entryId: EntryId, body: string): Promise<Comment> {
    const me = this.requireMe();
    const { data, error } = await this.sb
      .from('comments')
      .insert({ entry_id: entryId, crew_id: this.crewId, author_id: me.userId, body })
      .select('*')
      .single();
    if (error) throw new RepoError('INTERNAL', error.message);
    this.entryByComment.set(data.id, data.entry_id);
    return this.toComment(data);
  }

  // == media carousel =======================================================

  /**
   * The `quickAdd` flow, one table over: try the insert, and only on the FK
   * violation mint the provisional catalog row and retry once. Ordering it that
   * way (rather than calling `ensure_game` up front) means the overwhelmingly
   * common case — a video on a game the crew already has — costs one round trip,
   * and `ensure_game` is `on conflict do nothing` anyway, so the retry can never
   * overwrite a curated row.
   */
  async addGameVideo(
    game: Game,
    v: VideoDraft,
  ): Promise<GameVideo> {
    const me = this.requireMe();
    const appid = Number(game.appid);
    if (!Number.isSafeInteger(appid) || appid <= 0) {
      // D-02 again: a CheapShark-only row has no appid the FK can hold.
      throw new RepoError('NO_APPID', "NO STEAM PAGE — CAN'T ATTACH VIDEOS");
    }

    let row = await this.insertVideo(appid, me.userId, v);
    if (!row.ok && row.code === '23503') {
      await this.ensureGameFromCache(appid);
      row = await this.insertVideo(appid, me.userId, v);
    }
    if (!row.ok) {
      // The (crew_id, game_appid, provider, video_key) unique index. This is a
      // normal outcome, not a fault: somebody already posted that clip.
      if (row.code === '23505') throw new RepoError('DUPLICATE', 'ALREADY ADDED');
      // 23514 — a CHECK refused the row. Since 3006 the provider and key
      // constraints are per-provider, so the only way to land here is a link
      // the parser accepted and the database did not. That is a bad link from
      // the member's point of view, not an internal fault, and it must not
      // surface as an opaque rollback toast the way it would have before.
      // Literal rather than importing `VIDEO_BAD_URL_TOAST`: the repository
      // layer does not import from store slices, and `slices/videos.ts` pins
      // this exact string in its own test.
      if (row.code === '23514') throw new RepoError('BAD_URL', '✕ NOT A YOUTUBE OR TWITCH LINK');
      throw new RepoError('INTERNAL', row.message);
    }
    this.appidByVideo.set(row.data.id, appid);
    return this.toVideo(row.data);
  }

  private async insertVideo(
    appid: number,
    userId: string,
    v: VideoDraft,
  ): Promise<
    { ok: true; data: Row<'game_videos'> } | { ok: false; code: string; message: string }
  > {
    const { data, error } = await this.sb
      .from('game_videos')
      .insert({
        crew_id: this.crewId,
        game_appid: appid,
        author_id: userId,
        provider: v.provider,
        video_key: v.key,
        url: v.url,
        title: v.title,
      })
      .select('*')
      .single();
    if (error) return { ok: false, code: error.code ?? '', message: error.message };
    return { ok: true, data };
  }

  async removeGameVideo(id: string): Promise<void> {
    const { error } = await this.sb.from('game_videos').delete().eq('id', id);
    if (error) throw new RepoError('INTERNAL', error.message);
    this.appidByVideo.delete(id);
  }

  // == goty =================================================================

  async fetchGotyYears(userId: string): Promise<number[]> {
    const q = this.sb.from('goty_entries').select('year').eq('user_id', userId);
    const { data, error } = await (this.crewId ? q.eq('crew_id', this.crewId) : q);
    if (error) throw new RepoError('INTERNAL', error.message);
    const years = new Set<number>();
    for (const r of data ?? []) if (typeof r.year === 'number') years.add(r.year);
    return [...years].sort((a, b) => b - a);
  }

  async fetchMemberGoty(userKey: string): Promise<MemberGotyYear[]> {
    const { data, error } = await this.sb.rpc('crew_goty', { p_user_key: userKey });
    if (error) throw new RepoError('INTERNAL', error.message);
    const rows = (data ?? []) as Array<{ year: number; game_appid: number; tier: string | null; tier_pos: number | null; pot_pos: number }>;
    const byYear = new Map<number, typeof rows>();
    for (const r of rows) (byYear.get(r.year) ?? byYear.set(r.year, []).get(r.year)!).push(r);
    const rank: Record<string, number> = Object.fromEntries(TIERS.map((t, i) => [t, i]));
    const out: MemberGotyYear[] = [];
    for (const [year, ys] of byYear) {
      const potentials = [...ys].sort((a, b) => a.pot_pos - b.pot_pos).map((r) => r.game_appid as GameId);
      const tiers: Record<string, Tier> = {};
      for (const r of ys) {
        const t = asTier(r.tier);
        if (t !== null) tiers[String(r.game_appid)] = t;
      }
      const tierOrder = ys
        .filter((r) => r.tier !== null && rank[r.tier] !== undefined)
        .sort((a, b) => rank[a.tier as string]! - rank[b.tier as string]! || (a.tier_pos ?? 0) - (b.tier_pos ?? 0))
        .map((r) => r.game_appid as GameId);
      out.push({ year, potentials, tiers, tierOrder });
    }
    return out.sort((a, b) => b.year - a.year);
  }

  async addPot(appid: GameId): Promise<void> {
    const id = Number(appid);
    if (!Number.isSafeInteger(id) || id <= 0) throw new RepoError('NO_APPID');
    const { error } = await this.sb.rpc('add_goty_potential', { p_appid: id, p_year: this.boardYear });
    if (error) throw new RepoError('INTERNAL', error.message);
    if (!this.boards.potentials.includes(id)) {
      this.boards = { ...this.boards, potentials: [...this.boards.potentials, id] };
    }
  }

  /**
   * The store persists whole boards (P2-03). This diffs the patch against the
   * last known state and replays it as the incremental RPCs the schema is built
   * around — `add_goty_potential` for a new potential, `place_tier(appid, tier,
   * before)` for a placement or a reorder, `place_tier(appid, null)` for an
   * unplace or a skip (which is exactly what that branch does: clear the tier,
   * push to the back of the skip queue).
   *
   * DEVIATION (P4-04): a patch that is not a recognisable incremental op falls
   * back to a direct `goty_entries` upsert of computed positions. That path is
   * unreachable from the current UI; it exists so a future bulk edit cannot
   * silently no-op.
   *
   * SERIALIZED for the same reason `setIntAt` is — this is the other
   * read-modify-write against `this.boards`. See `serializeBoardWrite`.
   */
  async saveBoards(patch: Partial<MyBoards>): Promise<void> {
    return this.serializeBoardWrite(() => this.saveBoardsNow(patch));
  }

  private async saveBoardsNow(patch: Partial<MyBoards>): Promise<void> {
    const me = this.requireMe();
    const before = this.boards;
    const after: MyBoards = {
      potentials: patch.potentials ?? before.potentials,
      tiers: patch.tiers ?? before.tiers,
      tierOrder: patch.tierOrder ?? before.tierOrder,
      skips: patch.skips ?? before.skips,
      intOrder: patch.intOrder ?? before.intOrder,
      intSkips: patch.intSkips ?? before.intSkips,
    };

    const num = (v: GameId): number | null => {
      const n = Number(v);
      return Number.isSafeInteger(n) && n > 0 ? n : null;
    };

    // 1 — new potentials (steamFill adds several at once).
    const had = new Set(before.potentials.map(String));
    for (const id of after.potentials) {
      if (had.has(String(id))) continue;
      const n = num(id);
      if (n === null) continue;
      const { error } = await this.sb.rpc('add_goty_potential', { p_appid: n, p_year: this.boardYear });
      if (error) throw new RepoError('INTERNAL', error.message);
    }

    // 2 — removed potentials (no UI path today; kept so the diff is total).
    const keep = new Set(after.potentials.map(String));
    const dropped = before.potentials.filter((id) => !keep.has(String(id))).map(num);
    for (const n of dropped) {
      if (n === null) continue;
      await this.sb
        .from('goty_entries')
        .delete()
        .eq('user_id', me.userId)
        .eq('game_appid', n)
        .eq('year', this.boardYear);
    }

    // 3 — tier placements. Each tier row is rebuilt back-to-front so that when
    // an item is placed before its successor, the successor already sits where
    // the new order wants it.
    const beforeSeq = tierSequences(before);
    const afterSeq = tierSequences(after);
    const handled = new Set<string>();
    for (const tier of TIERS) {
      const seq = afterSeq[tier];
      const old = beforeSeq[tier];
      if (sameSeq(seq, old)) continue;
      for (let i = seq.length - 1; i >= 0; i--) {
        const id = seq[i];
        if (id === undefined) continue;
        if (old[i] === id) continue; // unchanged position in an unchanged prefix
        const n = num(id);
        if (n === null) continue;
        const succ = i + 1 < seq.length ? num(seq[i + 1] as GameId) : null;
        const args =
          succ === null
            ? { p_appid: n, p_tier: tier, p_year: this.boardYear }
            : { p_appid: n, p_tier: tier, p_before_appid: succ, p_year: this.boardYear };
        const { error } = await this.sb.rpc('place_tier', args);
        if (error) throw new RepoError('INTERNAL', error.message);
        handled.add(String(id));
      }
    }

    // 4 — unplacements and skips. `place_tier(appid, null)` clears the tier and
    // pushes the id to the back of the skip queue, which is both `unplaceTier`
    // and `skipTodoId` (prototype 1719 / 1725).
    const unplaced = new Set<string>();
    for (const id of Object.keys(before.tiers)) {
      if (after.tiers[id] === undefined) unplaced.add(id);
    }
    const oldSkips = new Set(before.skips.map(String));
    for (const id of after.skips) {
      const s = String(id);
      if (!oldSkips.has(s)) unplaced.add(s);
    }
    // A re-skip of an id already in the queue moves it to the back.
    const lastNew = after.skips.length ? String(after.skips[after.skips.length - 1]) : null;
    const lastOld = before.skips.length ? String(before.skips[before.skips.length - 1]) : null;
    if (lastNew && lastNew !== lastOld) unplaced.add(lastNew);

    for (const id of unplaced) {
      if (handled.has(id)) continue;
      const n = num(id);
      if (n === null) continue;
      const { error } = await this.sb.rpc('place_tier', { p_appid: n, p_year: this.boardYear });
      if (error) throw new RepoError('INTERNAL', error.message);
    }

    // `setIntAtNow`, NOT `setIntAt`: we are already inside the board-write
    // chain, and re-entering it would deadlock on a link we ourselves hold.
    if (patch.intOrder) await this.setIntAtNow(patch.intOrder);
    if (patch.intSkips) await this.writeIntSkips(before.intSkips, after.intSkips);
    this.boards = cloneBoards(after);
  }

  /**
   * The MY INTERESTS skip queue → `interest_order.skip_pos` (3037).
   *
   * There is no RPC for this and it does not want one: unlike a tier placement,
   * a skip has no neighbour arithmetic to get wrong — the queue is "these ids,
   * in this order", so the position IS the index and the write is idempotent.
   *
   * Only rows whose skip state actually MOVED are written — a five-game board
   * should not issue five writes because one game was deferred.
   *
   * UPDATE, then INSERT only if nothing matched — deliberately NOT an upsert.
   * `position` is NOT NULL, so an upsert row has to carry one, and writing an
   * integer over a row the `set_interest_order` RPC gave a FRACTIONAL position
   * (1.5, sitting between two neighbours) would silently reorder the board:
   * this write is about skips and must leave the ordering it found alone. The
   * insert branch is for a game the member has never dragged, which has no row
   * yet; it is born at the end of the order, where an unranked game already
   * sorts.
   */
  private async writeIntSkips(before: GameId[], after: GameId[]): Promise<void> {
    const me = this.requireMe();
    const num = (v: GameId): number | null => {
      const n = Number(v);
      return Number.isSafeInteger(n) && n > 0 ? n : null;
    };

    const beforePos = new Map(before.map((id, i) => [String(id), i + 1]));
    const afterPos = new Map(after.map((id, i) => [String(id), i + 1]));
    const order = this.boards.intOrder.map(String);

    for (const key of new Set<string>([...beforePos.keys(), ...afterPos.keys()])) {
      const next = afterPos.get(key) ?? null;
      if (next === (beforePos.get(key) ?? null)) continue;
      const n = num(key as GameId);
      if (n === null) continue;

      const { data, error } = await this.sb
        .from('interest_order')
        .update({ skip_pos: next })
        .eq('crew_id', this.crewId)
        .eq('user_id', me.userId)
        .eq('game_appid', n)
        .select('game_appid');
      if (error) throw new RepoError('INTERNAL', error.message);
      if (data && data.length) continue;

      // No row yet. Un-skipping one that does not exist is already true, so
      // only a real skip is worth minting a row for.
      if (next === null) continue;
      const at = order.indexOf(key);
      const { error: insErr } = await this.sb.from('interest_order').insert({
        user_id: me.userId,
        game_appid: n,
        crew_id: this.crewId,
        position: at >= 0 ? at + 1 : order.length + 1,
        skip_pos: next,
      });
      if (insErr) throw new RepoError('INTERNAL', insErr.message);
    }
  }

  /**
   * ARCHITECTURE §3.3, `steam-sync op=goty`: the year's most-played owned games,
   * retiring the prototype's hardcoded eight (L-06 / INVENTORY §12.11).
   *
   * The function reads Steam live rather than `steam_owned_apps`, so this works
   * on a linked account whose library has never been swept — which is the state
   * every account is in until the nightly cron or a CHECK NOW has run.
   */
  async steamFill(year: number): Promise<SteamFillResult> {
    const res = await this.invoke<{
      profile_public?: boolean;
      games?: Array<{ appid?: number; name?: string; playtime_min?: number; last_played_at?: string }>;
      dated?: boolean;
      candidates?: Array<{
        appid?: number; name?: string; playtime_min?: number;
        signal?: string; signals?: string[]; evidence_at?: string | null; confident?: boolean;
      }>;
      library?: Array<{ appid?: number; name?: string; playtime_min?: number }>;
      counts?: Record<string, unknown> | null;
      backfill?: Record<string, unknown> | null;
    }>('steam-sync', { op: 'goty', year });
    if (!res.ok) throw new RepoError(res.code, res.message);

    const int = (v: unknown): number => Math.max(0, Math.trunc(Number(v ?? 0)) || 0);

    const games: SteamFillGame[] = [];
    for (const g of res.data.games ?? []) {
      const appid = Number(g?.appid);
      if (!Number.isSafeInteger(appid) || appid <= 0) continue;
      const at = g?.last_played_at ? Date.parse(g.last_played_at) : NaN;
      games.push({
        appid,
        name: (g?.name ?? '').trim(),
        playtimeMin: int(g?.playtime_min),
        lastPlayedAt: Number.isFinite(at) ? at : 0,
      });
    }

    const candidates: GotyCandidate[] = [];
    for (const c of res.data.candidates ?? []) {
      const appid = Number(c?.appid);
      if (!Number.isSafeInteger(appid) || appid <= 0) continue;
      const at = c?.evidence_at ? Date.parse(c.evidence_at) : NaN;
      const signals = (c?.signals ?? []).filter(isGotySignal);
      const signal = isGotySignal(c?.signal) ? c.signal : signals[0];
      // A candidate whose signal we cannot read is a candidate we cannot label,
      // and an unlabelled row in a picker that exists to explain itself is worse
      // than no row. Drop rather than guess.
      if (signal === undefined) continue;
      candidates.push({
        appid,
        name: (c?.name ?? '').trim(),
        playtimeMin: int(c?.playtime_min),
        signal,
        signals: signals.length > 0 ? signals : [signal],
        evidenceAt: Number.isFinite(at) ? at : 0,
        // Absent reads as NOT confident: the picker pre-ticks on this, and the
        // safe default is to make the member opt in rather than opt out.
        confident: c?.confident === true,
      });
    }

    const library: GotyLibraryGame[] = [];
    for (const g of res.data.library ?? []) {
      const appid = Number(g?.appid);
      if (!Number.isSafeInteger(appid) || appid <= 0) continue;
      library.push({ appid, name: (g?.name ?? '').trim(), playtimeMin: int(g?.playtime_min) });
    }

    const raw = res.data.counts;
    const counts: GotySignalCounts | null = raw
      ? {
        owned: int(raw.owned),
        withPlaytime: int(raw.with_playtime),
        withStamp: int(raw.with_stamp),
        fromPlayed: int(raw.from_played),
        fromAchievement: int(raw.from_achievement),
        fromLedger: int(raw.from_ledger),
        fromRecent: int(raw.from_recent),
        fromReleased: int(raw.from_released),
        achievementsChecked: int(raw.achievements_checked),
        achievementsCapped: raw.achievements_capped === true,
      }
      : null;

    // `profile_public` is absent only on an envelope from a function older than
    // this contract; treating that as public keeps the games it did return.
    //
    // `dated` gets the OPPOSITE default, and deliberately: a function too old to
    // send it is one that only ever returned rtime-dated games, so `games` means
    // what it always meant and no picker should open behind it.
    const backfill = res.data.backfill ? backfillOf(res.data.backfill) : null;
    return {
      profilePublic: res.data.profile_public !== false,
      games,
      dated: res.data.dated !== false,
      candidates,
      library,
      counts,
      // Left OFF rather than set to a default when the function did not send
      // it: absent means "predates 3070", and the store reads that as nothing
      // to wait for.
      ...(backfill ? { backfill } : {}),
    };
  }

  async fetchSteamBackfill(): Promise<SteamBackfill | null> {
    // No `.eq('user_id', …)`: the table's only policy is self-select (3070), so
    // the caller's own row is the only one this can return.
    const { data, error } = await this.sb
      .from('steam_backfill_state')
      .select('status, done, total, completed_at')
      .maybeSingle();
    if (error || !data) return null;
    return backfillOf(data);
  }

  // == what's new ===========================================================

  async markReviewed(): Promise<void> {
    const me = this.requireMe();
    const stamp = new Date(this.now()).toISOString();
    const { error } = await this.sb
      .from('profiles')
      .update({ last_reviewed_at: stamp })
      .eq('id', me.userId);
    if (error) throw new RepoError('INTERNAL', error.message);
    this.me = { ...me, lastReviewedAt: Date.parse(stamp) };
  }

  /** `whats_new_seen` is keyed by entry, the store by game id (P2-02). */
  async seenUsers(gameIds: GameId[]): Promise<void> {
    const me = this.requireMe();
    const rows = gameIds
      .map((id) => this.entryByAppid.get(Number(id)))
      .filter((e): e is string => !!e)
      .map((entryId) => ({ user_id: me.userId, entry_id: entryId, crew_id: this.crewId }));
    if (!rows.length) return;
    const { error } = await this.sb
      .from('whats_new_seen')
      .upsert(rows, { onConflict: 'user_id,entry_id', ignoreDuplicates: true });
    if (error) throw new RepoError('INTERNAL', error.message);
  }

  async fetchEvents(sinceIso: string, limit: number): Promise<CrewEvent[]> {
    const { data, error } = await this.sb
      .from('crew_events')
      .select('*')
      .gt('created_at', sinceIso)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw new RepoError('INTERNAL', error.message);
    return (data ?? []).map((e) => this.toEvent(e));
  }

  // == playtests ============================================================

  /**
   * P6 F-2. The URL/appid validation chain stays local; the verdict comes from
   * `GET /api/steam/ptcheck/:appid`, which fetches and parses the real store
   * page. A 502 (age gate, redirect, changed markup, Worker down) is
   * `PT_CHECK_UNAVAILABLE` — never `widget:false`.
   */
  async ptDoCheck(url: string): Promise<PtCheckResult> {
    const base = ptCheckUrl(url, this.pts, (appid) => this.catalog.get(appid) ?? null);
    if (!base.ok) return base;
    try {
      const d = await this.api<PtCheckData>('/api/steam/ptcheck/' + base.appid);
      return ptCheckFound(base, d);
    } catch {
      return { ok: false, err: PT_CHECK_UNAVAILABLE };
    }
  }

  /**
   * P6 F-3. The row lands with the provenance the check actually produced.
   *
   * It used to hardcode `status:'open'` + `WATCHING FOR WAVES` +
   * `SOURCE: AUTO — STORE-WIDGET SCRAPE` on every insert, which claimed a
   * nightly scrape the row had never had — and, for an app with no widget, a
   * claim the scan would spend three nights "correcting" (P6 §2 rule 3), which
   * reads as a bug. `SOURCE: AUTO — STORE-WIDGET SCRAPE` now belongs to the scan
   * alone: it writes that string on its first confirming pass.
   */
  async ptDoTrack(input: PtSteamInput): Promise<Playtest> {
    const me = this.requireMe();
    const p = ptTrackFields(input);
    const { data, error } = await this.sb
      .from('playtests')
      .insert({
        crew_id: this.crewId,
        kind: 'steam',
        appid: input.appid > 0 ? input.appid : null,
        playtest_appid: input.playtestAppid > 0 ? input.playtestAppid : null,
        name: input.name,
        dev: input.dev,
        hue: input.hue,
        party_max: input.party,
        party_label: ptPartyLbl(input.party),
        status: p.status,
        foot: p.foot,
        auto: false,
        created_by: me.userId,
        note: p.note,
        url: input.appid ? 'https://store.steampowered.com/app/' + input.appid + '/' : '',
        dates_src: p.datesSrc,
      })
      .select('*')
      .single();
    if (error) throw new RepoError('INTERNAL', error.message);
    const pt = this.toPlaytest(data, {}, {});
    this.pts = [...this.pts, pt];
    return pt;
  }

  async ptDoAddOff(input: PtOffInput): Promise<Playtest> {
    const me = this.requireMe();
    let host = '';
    try {
      host = new URL(input.url).hostname.replace(/^www\./, '').toUpperCase();
    } catch {
      throw new RepoError('BAD_URL', 'THAT URL DOES NOT PARSE');
    }
    const { data, error } = await this.sb
      .from('playtests')
      .insert({
        crew_id: this.crewId,
        kind: 'off',
        appid: null,
        name: input.name,
        dev: '—',
        hue: (input.name.length * 47) % 360,
        party_max: input.party,
        party_label: ptPartyLbl(input.party),
        status: 'open',
        auto: false,
        created_by: me.userId,
        window_start: input.start ? input.start + 'T12:00:00Z' : null,
        window_end: input.end ? input.end + 'T12:00:00Z' : null,
        note:
          'Off-Steam signup — user-maintained, no auto-sync. Anyone can update; ' +
          'every edit is attributed.',
        url: input.url,
        src_label: host,
        dates_src: 'DATES: USER-ENTERED',
      })
      .select('*')
      .single();
    if (error) throw new RepoError('INTERNAL', error.message);
    const pt = this.toPlaytest(data, {}, {});
    this.pts = [...this.pts, pt];
    return pt;
  }

  async ptToggleInt(playtestId: string): Promise<boolean> {
    const me = this.requireMe();
    const existing = await this.sb
      .from('playtest_interest')
      .select('playtest_id')
      .eq('playtest_id', playtestId)
      .eq('user_id', me.userId)
      .maybeSingle();
    if (existing.error) throw new RepoError('INTERNAL', existing.error.message);

    if (existing.data) {
      const { error } = await this.sb
        .from('playtest_interest')
        .delete()
        .eq('playtest_id', playtestId)
        .eq('user_id', me.userId);
      if (error) throw new RepoError('INTERNAL', error.message);
      return false;
    }
    const { error } = await this.sb
      .from('playtest_interest')
      .insert({ playtest_id: playtestId, user_id: me.userId, crew_id: this.crewId });
    if (error) throw new RepoError('INTERNAL', error.message);
    return true;
  }

  async ptReport(playtestId: string, s: 'in' | 'req'): Promise<void> {
    const me = this.requireMe();
    const { error } = await this.sb.from('playtest_status').upsert(
      {
        playtest_id: playtestId,
        user_id: me.userId,
        crew_id: this.crewId,
        status: s,
        note: 'SELF-REPORTED ' + ptFmt(this.now()),
        source: 'self',
      },
      { onConflict: 'playtest_id,user_id' },
    );
    if (error) throw new RepoError('INTERNAL', error.message);
  }

  async ptClearSt(playtestId: string): Promise<void> {
    const me = this.requireMe();
    const { error } = await this.sb
      .from('playtest_status')
      .delete()
      .eq('playtest_id', playtestId)
      .eq('user_id', me.userId);
    if (error) throw new RepoError('INTERNAL', error.message);
  }

  /** D-04: an `auto` row is muted for me only; anything else is deleted crew-wide. */
  async ptUntrack(playtestId: string): Promise<{ muted: boolean }> {
    const me = this.requireMe();
    const pt = this.pts.find((p) => p.id === playtestId);
    const auto =
      pt?.auto ??
      (
        await this.sb.from('playtests').select('auto').eq('id', playtestId).maybeSingle()
      ).data?.auto ??
      false;

    if (auto) {
      const { error } = await this.sb
        .from('playtest_mutes')
        .upsert(
          { playtest_id: playtestId, user_id: me.userId, crew_id: this.crewId },
          { onConflict: 'playtest_id,user_id', ignoreDuplicates: true },
        );
      if (error) throw new RepoError('INTERNAL', error.message);
    } else {
      const { error } = await this.sb.from('playtests').delete().eq('id', playtestId);
      if (error) throw new RepoError('INTERNAL', error.message);
    }
    this.pts = this.pts.filter((p) => p.id !== playtestId);
    return { muted: auto };
  }

  /** Phase 6 replaces this with `playtest-scan {mode:'catalog'}` (ARCHITECTURE §3.4). */
  async ptBrowse(q: string): Promise<PtBrowseRow[]> {
    const nq = q.trim().toLowerCase();
    return PT_BROWSE.filter(
      (x) => !nq || x.name.toLowerCase().includes(nq) || x.dev.toLowerCase().includes(nq),
    ).map((x) => ({ ...x, tags: [...x.tags] }));
  }

  // == steam link ===========================================================

  /**
   * `steam-link op=start` mints the signed state and returns the
   * `steamcommunity.com` sign-in URL (PORTING-NOTES P5-02/P5-03 — the function
   * owns the OpenID `realm`/`return_to` pairing, because only it knows the
   * functions origin Steam will accept). `returnTo` is the app path to land on
   * afterwards; the function collapses anything non-local to `/` (P5-11).
   *
   * The caller performs the navigation. Doing it here would make a repository
   * method unwind the page out from under an in-flight optimistic write.
   */
  async steamLinkStart(returnTo: string): Promise<{ redirectUrl: string }> {
    let redirect = '/';
    try {
      redirect = new URL(returnTo, this.env.url).pathname || '/';
    } catch {
      redirect = '/';
    }
    const res = await this.invoke<{ url?: string }>('steam-link', { op: 'start', redirect });
    if (!res.ok || !res.data.url) throw new RepoError(res.ok ? 'INTERNAL' : res.code, 'steam-link');
    return { redirectUrl: res.data.url };
  }

  async steamUnlink(): Promise<void> {
    const res = await this.invoke('steam-link', { op: 'unlink' });
    if (!res.ok) {
      // Fall back to the rows the member is allowed to delete themselves, so an
      // "unlink" the user asked for is never silently a no-op.
      const me = this.requireMe();
      await this.sb.from('steam_links').delete().eq('user_id', me.userId);
    }
  }

  /**
   * Graceful degradation is a hard requirement (§3.3): this never throws.
   * `NO_KEY`, `NOT_LINKED`, `THROTTLED` and `PRIVATE` all come back as an
   * `ok:false` result carrying the code, which is what the sync sheet switches
   * on to choose its copy.
   */
  async steamSyncNow(): Promise<SyncResult> {
    const res = await this.invoke<{
      data?: {
        checked_at?: string;
        profile_public?: boolean;
        owned?: number;
        granted?: Array<{ playtest_id: string; name: string }>;
      };
    }>('steam-sync', { op: 'self' });
    if (!res.ok) {
      return {
        ok: false,
        checkedAt: this.now(),
        profilePublic: false,
        owned: 0,
        granted: [],
        code: res.code,
      };
    }
    const d = res.data.data ?? {};
    return {
      ok: true,
      checkedAt: d.checked_at ? Date.parse(d.checked_at) : this.now(),
      // A private profile is a SUCCESSFUL check whose answer is "game details
      // are private" (P5-05) — the UI reads this flag, not the ok flag, to show
      // `PRIVATE — GOT-IN DETECTION OFF`.
      profilePublic: d.profile_public ?? true,
      owned: d.owned ?? 0,
      granted: (d.granted ?? []).map((g) => ({ playtestId: g.playtest_id, name: g.name })),
    };
  }

  // == admin ================================================================

  async doAddUser(i: { name: string; email: string; role: Role }): Promise<Invite> {
    const body: Record<string, unknown> = { op: 'create', name: i.name, role: i.role };
    if (i.email) body['recovery_email'] = i.email;
    const res = await this.invoke<{
      display_name: string;
      user_key: string;
      temp_password: string;
      invite_link: string;
    }>('admin-users', body);
    if (!res.ok) throw new RepoError(res.code, res.message);
    return {
      name: res.data.display_name.toUpperCase(),
      pw: res.data.temp_password,
      link: res.data.invite_link,
      // The server has always sent this and we have always thrown it away —
      // which is how somebody could be handed a name that signs in as
      // somebody else. It is the only field on here they can actually type.
      key: res.data.user_key,
    };
  }

  // == invitations (3026, docs/INVITES.md) ==================================

  async inviteMember(email: string): Promise<InviteSent> {
    const res = await this.invoke<{
      email: string;
      delivery: 'email' | 'link';
      expires_at: string;
      invite_link?: string;
    }>('admin-users', { op: 'invite', email });
    if (!res.ok) throw new RepoError(res.code, res.message);
    return {
      email: res.data.email,
      delivery: res.data.delivery,
      expiresAt: res.data.expires_at,
      // Absent on the emailed path, deliberately — see `InviteSent`.
      ...(res.data.invite_link ? { link: res.data.invite_link } : {}),
    };
  }

  async listInvites(): Promise<PendingInvite[]> {
    const res = await this.invoke<{
      invites: Array<{
        email: string;
        invited_at: string;
        expires_at: string;
        delivery: 'email' | 'link';
        sent: boolean;
      }>;
    }>('admin-users', { op: 'invites' });
    if (!res.ok) throw new RepoError(res.code, res.message);
    return (res.data.invites ?? []).map((i) => ({
      email: i.email,
      invitedAt: i.invited_at,
      expiresAt: i.expires_at,
      delivery: i.delivery,
      sent: i.sent,
    }));
  }

  async revokeInvite(email: string): Promise<void> {
    const res = await this.invoke<{ email: string }>('admin-users', {
      op: 'revoke',
      email,
    });
    if (!res.ok) throw new RepoError(res.code, res.message);
  }

  /**
   * The three below hit `invite-accept`, which is `verify_jwt = false` because
   * the caller may have no account at all. `invoke` still attaches whatever
   * session exists, and the function uses it: signed in, accepting joins a
   * second crew instead of creating anything.
   */
  async peekInvite(token: string): Promise<InvitePeek> {
    const res = await this.invoke<{
      state: InvitePeek['state'];
      crew_name?: string;
      crew_slug?: string;
      inviter_name?: string;
      email_masked?: string;
      expires_at?: string;
    }>('invite-accept', { op: 'peek', token });
    if (!res.ok) throw new RepoError(res.code, res.message);
    return {
      state: res.data.state,
      crewName: res.data.crew_name ?? '',
      crewSlug: res.data.crew_slug ?? '',
      inviterName: res.data.inviter_name ?? '',
      emailMasked: res.data.email_masked ?? '',
      expiresAt: res.data.expires_at ?? '',
    };
  }

  async acceptInvite(i: {
    token: string;
    displayName?: string;
    password?: string;
  }): Promise<InviteAccepted> {
    const res = await this.invoke<{
      created_account: boolean;
      crew_id: string;
      crew_name: string;
      user_key?: string;
      email?: string;
    }>('invite-accept', {
      op: 'accept',
      token: i.token,
      ...(i.displayName ? { display_name: i.displayName } : {}),
      ...(i.password ? { password: i.password } : {}),
    });
    if (!res.ok) throw new RepoError(res.code, res.message);
    return {
      createdAccount: res.data.created_account,
      crewId: res.data.crew_id,
      crewName: res.data.crew_name,
      ...(res.data.user_key ? { userKey: res.data.user_key } : {}),
      ...(res.data.email ? { email: res.data.email } : {}),
    };
  }

  async declineInvite(token: string): Promise<void> {
    const res = await this.invoke<{ state: string }>('invite-accept', {
      op: 'decline',
      token,
    });
    if (!res.ok) throw new RepoError(res.code, res.message);
  }

  // == join links (3056, docs/JOIN-LINKS.md) ================================

  async partyJoinLink(): Promise<JoinLinkState> {
    const res = await this.invoke<{
      live: boolean;
      created_at: string | null;
      join_url?: string | null;
    }>('admin-users', { op: 'join_link' });
    if (!res.ok) throw new RepoError(res.code, res.message);
    return {
      live: res.data.live,
      createdAt: res.data.created_at,
      // Absent against an `admin-users` older than 3057 — the field is optional
      // for the same reason the admin dashboard's `mail` block is.
      joinUrl: res.data.join_url ?? null,
    };
  }

  async newPartyJoinLink(): Promise<JoinLinkMinted> {
    const res = await this.invoke<{ live: boolean; join_url: string }>('admin-users', {
      op: 'join_link_new',
    });
    if (!res.ok) throw new RepoError(res.code, res.message);
    return { live: res.data.live, joinUrl: res.data.join_url };
  }

  async offPartyJoinLink(): Promise<void> {
    const res = await this.invoke<{ live: boolean }>('admin-users', {
      op: 'join_link_off',
    });
    if (!res.ok) throw new RepoError(res.code, res.message);
  }

  async listJoinRequests(): Promise<JoinRequest[]> {
    const res = await this.invoke<{
      requests: Array<{ id: string; display_name: string; requested_at: string }>;
    }>('admin-users', { op: 'join_requests' });
    if (!res.ok) throw new RepoError(res.code, res.message);
    return (res.data.requests ?? []).map((r) => ({
      id: r.id,
      displayName: r.display_name,
      requestedAt: r.requested_at,
    }));
  }

  async decideJoinRequest(id: string, decision: 'approve' | 'deny'): Promise<void> {
    const res = await this.invoke<{ decision: string }>('admin-users', {
      op: 'join_decide',
      request_id: id,
      decision,
    });
    if (!res.ok) throw new RepoError(res.code, res.message);
  }

  /**
   * The six below hit `join-request`, which is `verify_jwt = false` because the
   * caller may have no account at all — the same reason `invite-accept` is, and
   * the same failure mode if it is deployed wrong (it presents as "the link is
   * broken" for everybody).
   *
   * `invoke` still attaches whatever session exists, and the function reads it:
   * that is how `request` tells a signed-in asker from a stranger without a
   * separate endpoint.
   */
  async peekJoinLink(token: string): Promise<JoinLinkPeek> {
    const res = await this.invoke<{
      state: JoinLinkPeek['state'];
      crew_name?: string;
      crew_slug?: string;
      leader_name?: string;
      already_member?: boolean;
    }>('join-request', { op: 'peek', token });
    if (!res.ok) throw new RepoError(res.code, res.message);
    return {
      state: res.data.state,
      crewName: res.data.crew_name ?? '',
      crewSlug: res.data.crew_slug ?? '',
      leaderName: res.data.leader_name ?? '',
      alreadyMember: res.data.already_member ?? false,
    };
  }

  async startJoinCode(token: string, email: string): Promise<JoinCodeSent> {
    const res = await this.invoke<{
      email_masked: string;
      expires_at: string;
      expires_minutes: number;
    }>('join-request', { op: 'start', token, email });
    if (!res.ok) throw new RepoError(res.code, res.message);
    return {
      emailMasked: res.data.email_masked,
      expiresAt: res.data.expires_at,
      expiresMinutes: res.data.expires_minutes,
    };
  }

  async verifyJoinCode(i: {
    token: string;
    email: string;
    code: string;
    displayName: string;
    password: string;
  }): Promise<JoinAsked> {
    const res = await this.invoke<{
      created_account: boolean;
      crew_name: string;
      email?: string;
    }>('join-request', {
      op: 'verify',
      token: i.token,
      email: i.email,
      code: i.code,
      display_name: i.displayName,
      password: i.password,
    });
    if (!res.ok) throw new RepoError(res.code, res.message);
    return {
      createdAccount: res.data.created_account,
      crewName: res.data.crew_name,
      ...(res.data.email ? { email: res.data.email } : {}),
    };
  }

  async requestJoin(token: string): Promise<JoinAsked> {
    const res = await this.invoke<{ created_account: boolean; crew_name: string }>(
      'join-request',
      { op: 'request', token },
    );
    if (!res.ok) throw new RepoError(res.code, res.message);
    return { createdAccount: res.data.created_account, crewName: res.data.crew_name };
  }

  async joinRequestStatus(): Promise<JoinStatus> {
    const res = await this.invoke<{
      waiting: boolean;
      crew_name?: string;
      crew_slug?: string;
      leader_name?: string;
      requested_at?: string;
    }>('join-request', { op: 'status' });
    if (!res.ok) throw new RepoError(res.code, res.message);
    return {
      waiting: res.data.waiting,
      crewName: res.data.crew_name ?? '',
      crewSlug: res.data.crew_slug ?? '',
      leaderName: res.data.leader_name ?? '',
      requestedAt: res.data.requested_at ?? '',
    };
  }

  async withdrawJoinRequest(): Promise<void> {
    const res = await this.invoke<{ waiting: boolean }>('join-request', {
      op: 'withdraw',
    });
    if (!res.ok) throw new RepoError(res.code, res.message);
  }

  async doResetPw(userKey: string): Promise<Invite> {
    const res = await this.invoke<{
      display_name: string;
      user_key: string;
      temp_password: string;
      invite_link: string;
    }>('admin-users', { op: 'reset', user_id: this.userIdOf(userKey) });
    if (!res.ok) throw new RepoError(res.code, res.message);
    return {
      name: res.data.display_name.toUpperCase(),
      pw: res.data.temp_password,
      link: res.data.invite_link,
      // `op=reset` is handed to the same person for the same purpose, so it
      // carries the same answer. Falls back to the key we asked about, which
      // is the one we already hold.
      key: res.data.user_key || userKey,
    };
  }


  /**
   * Rename a crew you own (3030). An authenticated RPC for the same reason
   * `transferOwnership` is one: the authorization it needs — "are you this
   * crew's owner" — is a row the caller can already see.
   *
   * Returns the STORED name, which is trimmed. Handing back the server's
   * version rather than the caller's is what keeps a trailing space out of the
   * switcher while the row underneath says something else.
   */
  async renameCrew(crewId: string, name: string): Promise<string> {
    const { data, error } = await this.sb.rpc('rename_crew', {
      p_crew_id: crewId,
      p_name: name,
    });
    if (error) {
      // 22023 is the two validation refusals (empty, too long) and 42501 is
      // "not yours". All three are written to be shown.
      throw new RepoError(error.code === '22023' ? 'BAD_REQUEST' : 'FORBIDDEN', error.message);
    }
    return (data as string | null) ?? name.trim();
  }

  async transferOwnership(userKey: string): Promise<void> {
    if (!this.crewId) throw new RepoError('NOT_SIGNED_IN', 'No active crew');
    // An authenticated RPC, not an edge function: the authorization it needs -
    // "are you this crew's owner" - is a row the caller can already see, so
    // there is nothing here that requires the service role.
    const { error } = await this.sb.rpc('transfer_crew_ownership', {
      p_crew_id: this.crewId,
      p_new_owner: this.userIdOf(userKey),
    });
    if (error) {
      // 3020 raises 42501 for both "not the owner" and "they have not finished
      // their first sign-in"; the message is the only thing that tells them
      // apart, and it is written to be shown.
      throw new RepoError('FORBIDDEN', error.message);
    }
  }

  async doRm(userKey: string): Promise<void> {
    const res = await this.invoke('admin-users', {
      op: 'remove',
      user_id: this.userIdOf(userKey),
    });
    if (!res.ok) throw new RepoError(res.code, res.message);
  }

  private userIdOf(userKey: string): string {
    const id = this.idByKey.get(userKey);
    if (!id) throw new RepoError('NOT_FOUND', 'No such member');
    return id;
  }

  /** `functions.invoke` with the envelope of `_shared/json.ts` unwrapped. */
  private async invoke<T>(
    fn: string,
    body: Record<string, unknown>,
  ): Promise<{ ok: true; data: T } | { ok: false; code: string; message: string }> {
    let payload: unknown;
    try {
      const { data, error } = await this.sb.functions.invoke(fn, { body });
      if (error) {
        const ctx = (error as { context?: unknown }).context;
        if (ctx instanceof Response) {
          try {
            payload = await ctx.json();
          } catch {
            payload = null;
          }
        }
        if (!payload) return { ok: false, code: 'INTERNAL', message: error.message };
      } else {
        payload = data;
      }
    } catch (e) {
      return { ok: false, code: 'OFFLINE', message: String(e) };
    }
    const p = payload as { ok?: boolean; code?: string; message?: string } | null;
    if (!p || p.ok !== true) {
      return {
        ok: false,
        code: p?.code ?? 'INTERNAL',
        message: p?.message ?? 'Edge function failed',
      };
    }
    return { ok: true, data: p as unknown as T };
  }

  // == steam facts (SWR) ====================================================

  private api<T>(path: string, init?: RequestInit): Promise<T> {
    return steamApi<T>({ apiBase: this.apiBase, fetchImpl: this.fetchImpl }, path, init);
  }

  async fetchLive(q: string, signal?: AbortSignal): Promise<LiveSearchResult> {
    const query = q.trim();
    if (query.length < 2) return { items: [], src: 'steam', fromNetwork: true };
    try {
      const data = await this.api<{ total: number; items: unknown[] }>(
        '/api/steam/search?q=' + encodeURIComponent(query),
        signal ? { signal } : undefined,
      );
      const known = new Set<GameId>();
      const norms = new Set<string>();
      for (const g of this.catalog.values()) {
        known.add(g.id);
        norms.add(normName(g.n));
      }
      const items = data.items
        .map(toLiveGame)
        .filter((g): g is Game => !!g && !known.has(g.id) && !norms.has(normName(g.n)))
        .slice(0, 12);
      for (const g of items) this.liveGames.set(g.appid, g);
      return { items, src: 'steam', fromNetwork: true };
    } catch {
      return { items: [], src: 'steam', fromNetwork: false };
    }
  }

  async fetchBrowse(q: BrowseQuery, signal?: AbortSignal): Promise<BrowsePageResult> {
    const p = browseParams(q);

    const empty: BrowsePageResult = {
      total: 0,
      items: [],
      assets: {},
      start: q.start,
      found: 0,
      capped: false,
      fromNetwork: false,
    };
    if (q.term === '' && q.tags.length === 0) return empty;

    let data: {
      total: number;
      rows: unknown[];
      start: number;
      found: number;
      capped: boolean;
    };
    try {
      data = await this.api('/api/steam/browse?' + p.toString(), signal ? { signal } : undefined);
    } catch {
      // `fromNetwork: false` rather than a throw, so the caller can degrade to
      // the local path and the freshness chip can stay honest about it.
      return empty;
    }

    const items: Game[] = [];
    const assets: AssetMap = {};
    for (const raw of data.rows) {
      const mapped = toBrowseGame(raw);
      if (!mapped) continue;
      items.push(mapped.game);
      if (mapped.lib !== '' || mapped.hdr !== '') {
        assets[String(mapped.game.appid)] = { lib: mapped.lib, hdr: mapped.hdr };
      }
      // THE REGISTRATION `quickAdd` DEPENDS ON. Adding a browse result to Games
      // Tonight inserts into `list_entries`, whose FK the games table has never
      // heard of; the 23503 retry calls `ensureGameFromCache`, which reads this
      // map. Without this line every browse row added to the list becomes a
      // permanent `Steam App #<appid>` with no name, no developer and no art —
      // in the crew-shared table, for everyone, forever.
      this.liveGames.set(mapped.game.appid, mapped.game);
    }

    return {
      total: data.total,
      items,
      assets,
      start: data.start,
      found: data.found,
      capped: data.capped === true,
      fromNetwork: true,
    };
  }

  async lookupSeats(appids: number[]): Promise<SeatCount[]> {
    if (appids.length === 0) return [];
    // ≤50 per the function's own cap; the store already chunks, this is a belt.
    const r = await this.invoke<{ counts: unknown }>('player-lookup', {
      appids: appids.slice(0, 50),
    });
    // Fail-soft is the CONTRACT (Repository.ts): an enrichment of a page
    // already on screen answers empty rather than throwing, whatever failed —
    // including a client deployed ahead of the function itself.
    if (!r.ok || !Array.isArray(r.data.counts)) return [];
    const out: SeatCount[] = [];
    for (const c of r.data.counts as Array<Record<string, unknown>>) {
      const appid = Number(c?.['appid']);
      const mn = Number(c?.['mn']);
      const mx = Number(c?.['mx']);
      if (!Number.isSafeInteger(appid) || appid <= 0) continue;
      if (!Number.isSafeInteger(mx) || mx <= 0) continue;
      out.push({
        appid,
        mn: Number.isSafeInteger(mn) && mn >= 1 ? mn : 1,
        mx,
        unb: c?.['unb'] === true,
      });
    }
    return out;
  }

  async fetchTagList(): Promise<SteamTag[]> {
    try {
      const data = await this.api<{ tags: SteamTag[]; hash: string }>('/api/steam/tags');
      return Array.isArray(data.tags) ? data.tags : [];
    } catch {
      // An empty vocabulary disables the picker; it must never throw a boot path.
      return [];
    }
  }

  /**
   * One page of the ranked feed. See `Repository.fetchDiscoverPage`.
   *
   * THE SENTINEL TRANSLATION IS THE ONE THING THIS METHOD MUST NOT GET WRONG.
   * `defaultFilters()` is `pr: [0, 60]`, which `passFlt` reads as INACTIVE with
   * 60 meaning "no upper bound". Forwarding those two numbers literally would
   * send `price_min: 0, price_max: 60` to a server that treats both as real
   * bounds — silently dropping every upcoming game (no settled price) and
   * everything over $60 from the DEFAULT screen, while the filter button
   * reported zero active filters. Absent means no bound, on both ends.
   */
  async fetchDiscoverPage(q: DiscoverQuery, signal?: AbortSignal): Promise<DiscoverPageResult> {
    const f = q.flt;
    const [lo, hi] = f.pr;
    const priceActive = lo > 0 || hi < 60;

    const p: Record<string, Json> = {
      q: q.q,
      seed: q.seed,
      limit: q.limit,
      sort: q.sort,
      min_players: f.p,
      disc: f.disc,
      st_demo: f.st.demo,
      st_ea: f.st.ea,
      st_rel: f.st.rel,
      st_unrel: f.st.unrel,
      pt: f.pt,
      xtags: q.excludes,
    };
    // Absent when off, like the price bounds and for the same reason: 0 is not
    // a rung, and sending it would ask the server to compare every row against
    // a floor of nothing. Migration 3040 reads this key.
    if (f.rs > 0) p['min_review_score'] = f.rs;
    // OWNED BY (3047). Absent when off, for the third time on this list and the
    // same reason: `[]` and "no key" mean the same thing to `v_ownby`, but only
    // one of them says so, and sending an empty array on every default page
    // load would put a filter nobody set into every request log.
    if (f.own.length > 0) p['owned_by'] = [...f.own];
    if (q.cursor) p['cursor'] = q.cursor;
    if (priceActive) {
      if (lo > 0) p['price_min'] = lo;
      // 60 is the "and up" stop, not a ceiling.
      if (hi < 60) p['price_max'] = hi;
    }

    const empty: DiscoverPageResult = {
      rows: [],
      assets: {},
      total: 0,
      hiddenByFilters: 0,
      poolN: 0,
      tagCounts: [],
      nextCursor: null,
      limitUsed: q.limit,
      personalized: false,
      reason: 'ok',
      seed: q.seed,
      fromNetwork: false,
    };

    let data: DiscoverPageRpc | null;
    try {
      const res = await (signal
        ? this.sb.rpc('discover_page', { p }).abortSignal(signal)
        : this.sb.rpc('discover_page', { p }));
      if (res.error) throw new Error(res.error.message);
      data = res.data as DiscoverPageRpc | null;
    } catch {
      // `fromNetwork: false` rather than a throw: the SLICE decides what the
      // screen says, and §5.6 requires it say something rather than degrade
      // silently into the Phase 1 path.
      return empty;
    }
    if (!data) return empty;

    const rows: Game[] = [];
    const assets: AssetMap = {};
    for (const raw of data.rows ?? []) {
      const g = this.toGame(raw as unknown as VGameRow);
      if (raw.owned === true) g.owned = true;
      if (typeof raw.why === 'string' && raw.why !== '') g.why = raw.why;
      rows.push(g);
      if (raw.art_lib || raw.art_hdr) {
        assets[String(g.appid)] = { lib: raw.art_lib ?? '', hdr: raw.art_hdr ?? '' };
        this.dbAssets.set(g.appid, { lib: raw.art_lib ?? '', hdr: raw.art_hdr ?? '' });
      }
      // THE REGISTRATION `quickAdd` DEPENDS ON — the same rule `fetchBrowse`
      // documents. A feed row added to Games Tonight hits `list_entries`' FK,
      // and the 23503 retry mints the games-table row from this cache. These
      // are real catalog rows, so they belong in `catalog` rather than in
      // `liveGames`.
      this.catalog.set(g.appid, g);
    }

    return {
      rows,
      assets,
      total: Number(data.total ?? 0),
      hiddenByFilters: Number(data.hidden_by_filters ?? 0),
      poolN: Number(data.pool_n ?? 0),
      tagCounts: Array.isArray(data.tag_counts) ? data.tag_counts : [],
      nextCursor: typeof data.next_cursor === 'string' ? data.next_cursor : null,
      // The limit the server ACTUALLY used after clamping, not the one asked
      // for: a clamped request must not permanently end pagination.
      limitUsed: Number(data.limit_used ?? q.limit),
      personalized: data.personalized === true,
      reason: REASONS.has(String(data.reason)) ? (data.reason as DiscoverReason) : 'ok',
      seed: Number(data.seed ?? q.seed),
      fromNetwork: true,
    };
  }

  /** P6 F-1 — see `Repository.fetchCsRows`. */
  async fetchCsRows(q: string, signal?: AbortSignal): Promise<unknown[]> {
    const d = await this.api<{ rows: unknown[] }>(
      '/api/cs/search?q=' + encodeURIComponent(q.trim()),
      signal ? { signal } : undefined,
    );
    return Array.isArray(d.rows) ? d.rows : [];
  }

  /**
   * One appdetails round trip, two products: the volatile fact set that gets
   * written back to `steam_facts`, and the carousel media that never does
   * (decision D2). Splitting them into two methods would double the request.
   */
  async enrich(appid: number, _light: boolean): Promise<EnrichResult> {
    const d = await this.api<Record<string, unknown>>('/api/steam/app/' + appid);
    return { patch: mapAppDetails(appid, d), media: mapAppMedia(d) };
  }

  /**
   * Tier 2/3 of §5.3. On a Worker failure the crew-shared art already in
   * `steam_facts` (tier 1, loaded by `fetchCatalog`) is served with
   * `fromNetwork: false` — the data is right and the chip still tells the truth.
   *
   * Each record also carries the optional ribbon flags of `types.ts#AssetRec`
   * (`up`/`ea`/`demo`/`dAid`) straight off the Worker; `steamFacts#revalidate`
   * merges them into `facts` and patches the matching catalog / live rows. The
   * `dbAssets` fallback below carries none, and does not need to: those appids
   * are catalog rows, and `v_games` already joined their flags in.
   */
  async giFlush(appids: number[]): Promise<AssetsResult> {
    const ids = [...new Set(appids)]
      .filter((a) => a > 0)
      .slice(0, 50)
      .sort((a, b) => a - b);
    if (!ids.length) return { assets: {}, fromNetwork: true };
    try {
      const assets = await this.api<AssetMap>('/api/steam/items', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ appids: ids }),
      });
      if (!Object.keys(assets).length) throw new RepoError('EMPTY');
      return { assets, fromNetwork: true };
    } catch {
      const assets: AssetMap = {};
      for (const a of ids) {
        const rec = this.dbAssets.get(a);
        if (rec) assets[String(a)] = rec;
      }
      return { assets, fromNetwork: false };
    }
  }

  /** The self-heal write path. It can only reach `steam_facts` (decision D2). */
  async saveFix(patches: SteamFactPatch[]): Promise<void> {
    if (!patches.length) return;
    const { error } = await this.sb.rpc('upsert_steam_facts', {
      rows: patches.map(factPatchToRow),
    });
    if (error) throw new RepoError('INTERNAL', error.message);
  }

  // == rounds (migration 3039, docs/ROUNDS.md) ===============================

  /**
   * One shape for every round RPC: the function either returns the round's
   * JSON (or SQL null), or raises with a readable reason — `leader only`,
   * `no quarters left`, `wheel has been spun` — which becomes the toast.
   *
   * The generated `Database` types predate 3039 (they are hand-maintained and
   * `expand-contract.mjs` skips the file), so the call is cast at the library
   * seam and the payload is parsed by `parseRound`, which tolerates anything.
   */
  private async roundRpc(fn: string, args?: Record<string, unknown>): Promise<RoundSnapshot | null> {
    const { data, error } = await this.sb.rpc(fn as never, (args ?? {}) as never);
    if (error) throw new RepoError(roundErrCode(error.code), error.message);
    return parseRound(data);
  }

  async serverNow(): Promise<number> {
    const { data, error } = await this.sb.rpc('server_now' as never);
    if (error) throw new RepoError('INTERNAL', error.message);
    const t = typeof data === 'string' ? Date.parse(data) : NaN;
    if (!Number.isFinite(t)) throw new RepoError('INTERNAL', 'server_now returned no time');
    return t;
  }

  async roundGet(): Promise<RoundSnapshot | null> {
    return this.roundRpc('round_get');
  }
  async roundStart(): Promise<RoundSnapshot | null> {
    return this.roundRpc('round_start');
  }
  async roundJoin(): Promise<RoundSnapshot | null> {
    return this.roundRpc('round_join');
  }
  async roundLeave(): Promise<RoundSnapshot | null> {
    return this.roundRpc('round_leave');
  }
  async roundKick(userKey: string): Promise<RoundSnapshot | null> {
    return this.roundRpc('round_kick', { p_user_key: userKey });
  }
  async roundNominate(appid: number): Promise<RoundSnapshot | null> {
    return this.roundRpc('round_nominate', { p_appid: appid });
  }
  async roundClearMine(): Promise<RoundSnapshot | null> {
    return this.roundRpc('round_clear_mine');
  }
  async roundSetWeighted(weighted: boolean): Promise<RoundSnapshot | null> {
    return this.roundRpc('round_set_weighted', { p_weighted: weighted });
  }
  async roundSpin(): Promise<RoundSnapshot | null> {
    return this.roundRpc('round_spin');
  }
  async roundLock(): Promise<RoundSnapshot | null> {
    return this.roundRpc('round_lock');
  }
  async roundUnlock(): Promise<RoundSnapshot | null> {
    return this.roundRpc('round_unlock');
  }
  async roundUndoSpin(): Promise<RoundSnapshot | null> {
    return this.roundRpc('round_undo_spin');
  }
  async roundReset(): Promise<RoundSnapshot | null> {
    return this.roundRpc('round_reset');
  }
  async roundEnd(): Promise<void> {
    await this.roundRpc('round_end');
  }

  // == SCHEDULE (3071) ======================================================

  /**
   * One shape for every schedule write: the RPC returns the night's JSON or
   * raises with a readable reason (`already nominated for this night`, `only
   * the nominator can remove it`), which becomes the toast. `22023` is a
   * malformed window — the client's slider cannot make one, so reaching it is
   * a rule refusal too, not a network fault for `mutate.ts` to retry.
   */
  private async schedRpc(fn: string, args: Record<string, unknown>): Promise<unknown> {
    const { data, error } = await this.sb.rpc(fn as never, args as never);
    if (error) throw new RepoError(error.code === '22023' ? 'FORBIDDEN' : roundErrCode(error.code), error.message);
    return data;
  }

  async schedGet(from: string, to: string): Promise<SchedMap> {
    return parseSchedMap(await this.schedRpc('schedule_get', { p_from: from, p_to: to }));
  }
  async schedNominate(night: string, appid: number, from: number | null, to: number | null): Promise<SchedNom[]> {
    return parseSchedNight(await this.schedRpc('schedule_nominate', { p_night: night, p_appid: appid, p_from: from, p_to: to }));
  }
  async schedBack(night: string, appid: number): Promise<SchedNom[]> {
    return parseSchedNight(await this.schedRpc('schedule_back', { p_night: night, p_appid: appid }));
  }
  async schedSetRange(night: string, appid: number, from: number | null, to: number | null): Promise<SchedNom[]> {
    return parseSchedNight(await this.schedRpc('schedule_set_range', { p_night: night, p_appid: appid, p_from: from, p_to: to }));
  }
  async schedRemove(night: string, appid: number): Promise<SchedNom[]> {
    return parseSchedNight(await this.schedRpc('schedule_remove', { p_night: night, p_appid: appid }));
  }
  async schedStart(night: string, appids: number[]): Promise<RoundSnapshot | null> {
    return this.roundRpc('schedule_start_night', { p_night: night, p_appids: appids });
  }

  // == realtime =============================================================

  /**
   * §2.1 `crew:<crew_id>`. One binding per table, each filtered to the crew, so
   * the socket never carries another crew's rows even before RLS runs.
   *
   * §2.2: `SUBSCRIBED` fires on the initial connect **and on every reconnect**,
   * and events during a disconnect are lost forever — so the consumer's
   * `onStatus('subscribed')` handler must refetch the snapshot. That is not
   * optional, and it is why this method reports status at all. (Reconnects do
   * re-fire it: phoenix's `rejoin()` goes through `Push.resend`, and `reset()`
   * leaves `recHooks` alone, so the `receive('ok')` hook survives.)
   *
   * THE THREE STATUSES ARE NOT THREE SHADES OF THE SAME THING. `reconnecting`
   * is transient and self-healing; `error` is terminal and is not.
   *
   *   CHANNEL_ERROR / TIMED_OUT → 'reconnecting'.  The channel is phoenix's
   *     `errored`, which is a state it RETRIES OUT OF: `rejoinTimer` reschedules
   *     while the socket is up, and `socket.onOpen` rejoins every errored
   *     channel. A dropped socket lands here (`triggerChanError`), so the
   *     ordinary case — laptop lid, tunnel, wifi flap — needs nothing from us.
   *
   *   CLOSED → 'error'.  Phoenix's close hook runs `this.socket.remove(this)`,
   *     so the channel is not merely idle, it is OFF THE SOCKET'S LIST: no
   *     rejoin timer, no `onOpen` sweep, nothing. And `subscribe()` here is a
   *     no-op unless the channel `isClosed()` — the adapter guards it — so the
   *     object cannot be revived either. The only way back is a NEW channel,
   *     which means calling this method again. `store/boot.ts` does, on a
   *     backoff; without it a crew that hit this stopped receiving updates for
   *     the rest of the session and the ◆ badge silently froze.
   *
   * Reachable ways to land in `error`: a server `phx_close`, and
   * `RealtimeChannel._updatePostgresBindings` calling `unsubscribe()` when the
   * server hands back a binding list that does not match what we asked for.
   */
  subscribeCrew(_crewId: string, h: CrewHandlers): Unsub {
    const crew = this.crewId;
    if (!crew) return () => undefined;

    // Supersede the live subscription before building its replacement — and via
    // `retireCrew`, so the outgoing channel's CLOSED cannot be mistaken for the
    // new one failing. See the field's comment.
    this.retireCrew?.();
    const ch = this.sb.channel('crew:' + crew);
    const filter = 'crew_id=eq.' + crew;

    /**
     * `rowFilter` overrides the crew filter for tables that are not keyed by
     * crew_id (phase C, D2).
     *
     * Passing `null` means NO server-side filter, which is correct rather
     * than lax: Realtime authorizes each row against RLS, so an unfiltered
     * `profiles` binding delivers exactly the rows `profiles_select` already
     * admits — self plus this crew's members. Filtering on `profiles.crew_id`
     * instead, as this did, delivered a second-crew member's edits only to
     * their HOME crew's channel, and would break outright in phase E when the
     * column goes.
     *
     * `null`, NOT `undefined` (Marcus, staging, 2026-09-04: "I have to
     * refresh to see new recommendations"). This parameter used to default
     * to the crew filter, and a JavaScript default parameter fires for an
     * EXPLICIT `undefined` too — so every "unfiltered" binding below was in
     * fact filtered on `crew_id`, which `game_scores`, `board_events` and
     * `profiles` do not even carry, and Realtime matched nothing. Omitting the
     * argument still means the crew filter.
     */
    const bind = (
      table: string,
      fn: (p: PgPayload) => void,
      rowFilter: string | null = filter,
    ): void => {
      ch.on(
        // supabase-js types `postgres_changes` through a literal union it does
        // not export, and the filter object is generic over the table name. The
        // casts are at the library seam only; the payload is typed below.
        'postgres_changes' as never,
        {
          event: '*',
          schema: 'public',
          table,
          ...(rowFilter === null ? {} : { filter: rowFilter }),
        } as never,
        ((payload: PgPayload) => {
          try {
            fn(payload);
          } catch {
            // A patch we cannot translate must never kill the socket; fall back
            // to the coarse signal, which makes the consumer refetch.
            h.onChange({ table, actorKey: '' });
          }
        }) as never,
      );
    };

    const emit = (p: CrewPatch | null, table: string, actorId: string | null): void => {
      if (p && h.onPatch) {
        h.onPatch(p);
        return;
      }
      h.onChange({ table, actorKey: (actorId && this.keyById.get(actorId)) || '' });
    };

    bind('list_entries', (ev) => {
      if (ev.eventType === 'DELETE') {
        const old = ev.old as { id?: string } | null;
        if (!old?.id) throw new Error('no old row');
        this.forgetEntry(old.id);
        emit({ t: 'entry.remove', entryId: old.id }, 'list_entries', null);
        return;
      }
      const row = ev.new as Row<'list_entries'>;
      this.appidByEntry.set(row.id, row.game_appid);
      this.entryByAppid.set(row.game_appid, row.id);
      // §2.1: an appid the local catalog has never seen needs one catalog
      // fetch — `fetchOneGame`, which goes through `games_bootstrap` so the
      // row arrives with the same serializer decorations every other path gets.
      if (!this.catalog.has(row.game_appid) && h.onPatch) {
        void this.fetchOneGame(row.game_appid).then((g) => {
          if (g) h.onPatch?.({ t: 'catalog.upsert', game: g });
        });
      }
      emit(
        { t: 'entry.upsert', entry: this.blankEntry(row.id, row.game_appid, row.added_by, row.added_at) },
        'list_entries',
        row.added_by,
      );
      // A game arriving on the list may bring calls with it — anything the crew
      // said about it before it was last removed (3004). Those rows are already
      // in the table, so no server event announces them; this fetch is how
      // everyone EXCEPT the re-adder learns they exist. Same fire-and-forget
      // shape as the catalog fetch above, and the same degradation: if it
      // fails, the next SUBSCRIBED snapshot folds them in.
      if (ev.eventType === 'INSERT' && h.onPatch) {
        void this.fetchVoteBag(row.game_appid).then((bag) => {
          for (const [userKey, value] of Object.entries(bag)) {
            h.onPatch?.({ t: 'vote.set', entryId: row.id, userKey, value });
          }
        });
      }
    });

    bind('votes', (ev) => {
      // On DELETE this is PK-only — but the PK IS (crew_id, game_appid,
      // user_id) since 3004, which is everything the patch needs.
      const row = (ev.eventType === 'DELETE' ? ev.old : ev.new) as Row<'votes'> | null;
      if (!row?.game_appid || !row.user_id) throw new Error('no row');
      const key = this.keyById.get(row.user_id);
      if (!key) throw new Error('unknown member');
      // A call on a game that is not currently listed is legal now: it is a
      // remembered call, or one that raced a removal. There is no entry to
      // attach it to and nothing on screen it could change, so return rather
      // than throw — throwing here would trip the catch in `bind` and drag the
      // whole crew through a snapshot refetch for a patch that means nothing to
      // them. It is folded back in by `entryByAppid` if the game returns.
      const entryId = this.entryByAppid.get(row.game_appid);
      if (entryId === undefined) return;
      emit(
        {
          t: 'vote.set',
          entryId,
          userKey: key,
          value: ev.eventType === 'DELETE' ? null : normalizeVote(row.value),
        },
        'votes',
        row.user_id,
      );
    });

    bind('comments', (ev) => {
      if (ev.eventType === 'DELETE') {
        const old = ev.old as Partial<Row<'comments'>> | null;
        if (!old?.id) throw new Error('no old row');
        // `old` is PK-only under RLS, so the entry comes from the local map.
        const entryId = old.entry_id ?? this.entryByComment.get(old.id);
        if (!entryId) throw new Error('unknown comment');
        this.entryByComment.delete(old.id);
        emit({ t: 'comment.remove', entryId, commentId: old.id }, 'comments', null);
        return;
      }
      const row = ev.new as Row<'comments'>;
      this.entryByComment.set(row.id, row.entry_id);
      if (row.deleted_at) {
        emit({ t: 'comment.remove', entryId: row.entry_id, commentId: row.id }, 'comments', null);
        return;
      }
      emit(
        { t: 'comment.upsert', entryId: row.entry_id, comment: this.toComment(row) },
        'comments',
        row.author_id,
      );
    });

    bind('game_videos', (ev) => {
      if (ev.eventType === 'DELETE') {
        const old = ev.old as Partial<Row<'game_videos'>> | null;
        if (!old?.id) throw new Error('no old row');
        // PK-only under RLS, so the appid comes from the local map — the patch
        // is keyed by game and the store cannot look up a row that is gone.
        const appid = old.game_appid ?? this.appidByVideo.get(old.id);
        if (appid === undefined) throw new Error('unknown video');
        this.appidByVideo.delete(old.id);
        emit({ t: 'video.remove', videoId: old.id, appid }, 'game_videos', null);
        return;
      }
      const row = ev.new as Row<'game_videos'>;
      this.appidByVideo.set(row.id, row.game_appid);
      emit({ t: 'video.upsert', video: this.toVideo(row) }, 'game_videos', row.author_id);
    });

    bind('playtests', (ev) => {
      if (ev.eventType === 'DELETE') {
        const old = ev.old as { id?: string } | null;
        if (!old?.id) throw new Error('no old row');
        this.pts = this.pts.filter((p) => p.id !== old.id);
        emit({ t: 'playtest.remove', playtestId: old.id }, 'playtests', null);
        return;
      }
      const row = ev.new as PlaytestRow;
      const prev = this.pts.find((p) => p.id === row.id);
      const pt = this.toPlaytest(row, prev?.int ?? {}, prev?.st ?? {});
      this.pts = prev
        ? this.pts.map((p) => (p.id === pt.id ? pt : p))
        : [...this.pts, pt];
      emit({ t: 'playtest.upsert', playtest: pt }, 'playtests', row.created_by);
    });

    bind('playtest_interest', (ev) => {
      const row = (ev.eventType === 'DELETE' ? ev.old : ev.new) as Row<'playtest_interest'> | null;
      if (!row?.playtest_id || !row.user_id) throw new Error('no row');
      const key = this.keyById.get(row.user_id);
      if (!key) throw new Error('unknown member');
      emit(
        {
          t: 'pt.int',
          playtestId: row.playtest_id,
          userKey: key,
          at: ev.eventType === 'DELETE' ? null : Date.parse(row.marked_at),
        },
        'playtest_interest',
        row.user_id,
      );
    });

    bind('playtest_status', (ev) => {
      const row = (ev.eventType === 'DELETE' ? ev.old : ev.new) as Row<'playtest_status'> | null;
      if (!row?.playtest_id || !row.user_id) throw new Error('no row');
      const key = this.keyById.get(row.user_id);
      if (!key) throw new Error('unknown member');
      emit(
        {
          t: 'pt.status',
          playtestId: row.playtest_id,
          userKey: key,
          status:
            ev.eventType === 'DELETE'
              ? null
              : { s: row.status === 'in' ? 'in' : 'req', m: row.note },
        },
        'playtest_status',
        row.user_id,
      );
    });

    // Membership is what "in this crew" means now (D9): joins, departures and
    // per-crew hue/col/role all land here rather than on `profiles`. The
    // coarse signal is deliberate — a membership row carries no user_key, so
    // there is nothing to build a `member.upsert` patch out of, and a refetch
    // is both correct and rare.
    bind('crew_members', () => {
      h.onChange({ table: 'crew_members', actorKey: '' });
    });

    // The party's scores (3060). UNFILTERED, like `profiles` below: the table
    // has no crew_id, and Realtime authorizes each row against
    // `game_scores_select`, so a member receives exactly the crew's scores.
    // Coarse signal — the store refetches the small table whole rather than
    // patching a row, which is what the notice board's 14-day window wants.
    bind('game_scores', () => h.onChange({ table: 'game_scores', actorKey: '' }), null);
    // Recommendations (3062): same idiom — the policy hands each member the
    // sends they are party to, and the store refetches the set whole.
    bind('recommendations', () => h.onChange({ table: 'recommendations', actorKey: '' }), null);
    // The notice board's PARTY rows (3063): unfiltered, policy-scoped, same
    // idiom. WORLD rows ride the personal channel below.
    bind('board_events', () => h.onChange({ table: 'board_events', actorKey: '' }), null);

    bind('profiles', (ev) => {
      if (ev.eventType === 'DELETE') {
        const old = ev.old as ProfileRow | null;
        const key = old?.user_key ?? (old?.id ? this.keyById.get(old.id) : undefined);
        if (!key) throw new Error('no old row');
        this.keyById.delete(old?.id ?? '');
        this.idByKey.delete(key);
        emit({ t: 'member.remove', userKey: key }, 'profiles', null);
        return;
      }
      const row = ev.new as ProfileRow;
      this.keyById.set(row.id, row.user_key);
      this.idByKey.set(row.user_key, row.id);
      if (this.me && row.id === this.me.userId) {
        this.me = {
          ...this.me,
          displayName: row.display_name,
          role: row.role === 'admin' ? 'admin' : 'user',
          mustReset: row.must_reset,
          lastReviewedAt: Date.parse(row.last_reviewed_at),
        };
      }
      emit({ t: 'member.upsert', user: this.toUser(row, []) }, 'profiles', row.id);
    }, null);

    // Rounds (3039): three tables, one coarse patch. `crew_id` is denormalised
    // onto all three exactly so this filter works, and replica identity is
    // FULL so a DELETE (a pulled nomination, a member leaving) still carries
    // it. The store answers a `round.changed` with one `round_get()`.
    for (const table of ['rounds', 'round_members', 'round_noms']) {
      bind(table, () => emit({ t: 'round.changed' }, table, null));
    }

    // SCHEDULE (3071): two tables, one coarse patch, the round's reasoning —
    // `crew_id` on both, REPLICA IDENTITY FULL so a pulled quarter's DELETE
    // still carries it. The store answers with one `schedule_get()` over the
    // span it is showing.
    for (const table of ['schedule_noms', 'schedule_backers']) {
      bind(table, () => emit({ t: 'sched.changed' }, table, null));
    }

    // §2.4: every cross-member notification derives from crew_events, so there
    // is one code path, one throttle, and copy the triggers guarantee is true.
    ch.on(
      'postgres_changes' as never,
      { event: 'INSERT', schema: 'public', table: 'crew_events', filter } as never,
      ((payload: PgPayload) => {
        const row = payload.new as Row<'crew_events'>;
        const ev = this.toEvent(row);
        if (h.onEvent) h.onEvent(ev);
        else h.onChange({ table: 'crew_events', actorKey: ev.actorKey });
      }) as never,
    );

    // Flipped by `retireCrew` below. Every status this channel reports after
    // that point is the sound of our own teardown and belongs to nobody.
    let live = true;

    ch.subscribe((status) => {
      if (!live) return;
      if (status === 'SUBSCRIBED') h.onStatus?.('subscribed');
      else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') h.onStatus?.('reconnecting');
      else if (status === 'CLOSED') h.onStatus?.('error');
    });

    this.crewChannel = ch;
    const retire = (): void => {
      live = false;
      if (this.crewChannel === ch) this.crewChannel = null;
      if (this.retireCrew === retire) this.retireCrew = null;
      void this.sb.removeChannel(ch);
    };
    this.retireCrew = retire;
    return retire;
  }

  /**
   * §2.1 `me:<user_id>` — the personal boards, for multi-tab consistency. The
   * payloads are per-row but the boards are cheap to refetch and the store holds
   * them as whole arrays, so this reports the coarse signal deliberately.
   */
  subscribePersonal(userId: string, h: PersonalHandlers): Unsub {
    if (!this.personalRealtime || !userId) return () => undefined;
    if (this.personalChannel) void this.sb.removeChannel(this.personalChannel);

    const ch = this.sb.channel('me:' + userId);
    const filter = 'user_id=eq.' + userId;
    // `backlog` and `game_scores` (3060): your own rows, for a second tab of
    // yours. A crewmate's score arrives on the crew channel, not here.
    for (const table of ['goty_entries', 'interest_order', 'playtest_mutes', 'backlog', 'game_scores', 'game_watch', 'board_events']) {
      ch.on(
        'postgres_changes' as never,
        { event: '*', schema: 'public', table, filter } as never,
        (() => h.onChange({ table })) as never,
      );
    }
    ch.subscribe();

    this.personalChannel = ch;
    return () => {
      if (this.personalChannel === ch) this.personalChannel = null;
      void this.sb.removeChannel(ch);
    };
  }

  // == mapping ==============================================================

  private requireMe(): Profile {
    if (!this.me) throw new RepoError('NOT_SIGNED_IN');
    return this.me;
  }

  // == the board-write chain ================================================

  /**
   * Board writes run ONE AT A TIME, and this chain is what enforces it.
   *
   * `setIntAt` and `saveBoards` are both read-modify-write against
   * `this.boards`: they diff the array the store handed them against the last
   * one they know the server has, and replay the DIFFERENCE as incremental
   * RPCs. Two of them open at once and the diff is nonsense — the second one's
   * `prev` predates the first one's write, so the second rating reads as a
   * two-item change, `singleMove` gives up, and the fallback rewrites EVERY row
   * of the interest order.
   *
   * `mutate` does not prevent this and cannot: it serializes by key, and two
   * ratings on two different games are two different keys. Rating quickly is
   * exactly the shape that gets two commits open at once.
   *
   * The cost is not the write — an idempotent 1..n upsert is cheap and correct.
   * The cost is the ECHO: `subscribePersonal` reports one event PER ROW, so an
   * N-game order rewritten is N `refreshBoards` calls, two GETs each, and each
   * one landing makes the NEXT rating take the same fallback. That feedback
   * loop is what exhausted the browser's socket pool:
   *
   *     GET .../interest_order?select=*&user_id=eq.… net::ERR_INSUFFICIENT_RESOURCES
   *
   * `store/boot.ts#refreshBoards` caps the read side; this caps the write side.
   */
  private boardWrite: Promise<unknown> = Promise.resolve();
  /** Board writes enqueued but not yet settled. */
  private boardWritesOpen = 0;
  /** Bumped on every enqueue, so a read can tell one happened underneath it. */
  private boardWriteEpoch = 0;

  private serializeBoardWrite<T>(run: () => Promise<T>): Promise<T> {
    this.boardWritesOpen++;
    this.boardWriteEpoch++;
    const go = async (): Promise<T> => {
      try {
        return await run();
      } finally {
        this.boardWritesOpen--;
      }
    };
    // Both arms, so one failed write does not wedge the chain behind it — the
    // same bargain `mutate`'s queue makes.
    const next = this.boardWrite.then(go, go);
    this.boardWrite = next.catch(() => undefined);
    return next;
  }

  private clearCaches(): void {
    this.keyById.clear();
    this.idByKey.clear();
    this.appidByEntry.clear();
    this.entryByAppid.clear();
    this.entryByComment.clear();
    this.appidByVideo.clear();
    this.pts = [];
    this.boards = emptyBoards();
  }

  private forgetEntry(entryId: string): void {
    const appid = this.appidByEntry.get(entryId);
    this.appidByEntry.delete(entryId);
    if (appid !== undefined) this.entryByAppid.delete(appid);
  }

  private blankEntry(
    entryId: string,
    appid: number,
    addedBy: string | null,
    addedAt: string,
  ): { id: GameId; entryId: EntryId; by: string; at: number; votes: Record<string, VoteValue>; comments: Comment[] } {
    return {
      id: appid,
      entryId,
      // A nulled attribution renders as the prototype's FORMER FRIEND chip.
      by: (addedBy && this.keyById.get(addedBy)) || '',
      at: Date.parse(addedAt),
      votes: {},
      comments: [],
    };
  }

  private toComment(c: Row<'comments'>): Comment {
    return {
      id: c.id,
      by: (c.author_id && this.keyById.get(c.author_id)) || '',
      at: Date.parse(c.created_at),
      t: c.body,
    };
  }

  /** Same attribution rule as `toComment`: a nulled author is FORMER FRIEND. */
  private toVideo(v: Row<'game_videos'>): GameVideo {
    return {
      id: v.id,
      appid: v.game_appid,
      // `kind` is not a column: VOD vs clip is derivable from the stored link,
      // and a column would be a second place for the two to disagree.
      provider: v.provider === 'twitch' ? 'twitch' : 'youtube',
      kind: v.provider === 'twitch' ? (v.url.includes('/clip') ? 'clip' : 'vod') : 'video',
      key: v.video_key,
      url: v.url,
      title: v.title,
      by: (v.author_id && this.keyById.get(v.author_id)) || '',
      at: Date.parse(v.created_at),
    };
  }

  /**
   * `m` is the caller's `crew_members` row for THIS crew (phase C).
   *
   * hue, col and role are per-crew facts, so the membership wins wherever it
   * exists. The `profiles` columns remain the fallback for exactly one case —
   * an account visible through `profiles_select`'s legacy arm with no
   * membership row — and phase E deletes both the fallback and the columns.
   *
   * `role` still comes out as the legacy 'admin' | 'user' because that is what
   * `User.role` is typed as and what every surface switches on; owner maps to
   * admin. Renaming the union is phase D's job, with the surfaces.
   */
  private toUser(
    p: ProfileRow,
    seen: GameId[],
    m?: MembershipRow,
    loginEmails?: Map<string, string>,
  ): User {
    return {
      k: p.user_key,
      name: p.display_name,
      role: m ? (m.role === 'owner' ? 'admin' : 'user') : p.role === 'admin' ? 'admin' : 'user',
      hue: m ? m.hue : p.hue,
      col: (m ? m.col : p.col) ?? '',
      email: p.recovery_email ?? '',
      loginEmail: this.realLoginEmail(loginEmails?.get(p.id) ?? ''),
      mustReset: p.must_reset,
      av: this.avatarUrl(p),
      lastRev: Date.parse(p.last_reviewed_at),
      seen,
    };
  }

  /**
   * A synthesized `<user_key>@<domain>` address is not an address — it is the
   * absence of one, at a domain with no MX — so it collapses to `''` here
   * rather than being shown.
   *
   * The domain lives in env config, which is why this is the repository's job
   * and not a selector's: only this layer knows what "placeholder" means for
   * the environment it is talking to.
   */
  private realLoginEmail(email: string): string {
    const v = email.toLowerCase();
    return v && !v.endsWith('@' + this.env.emailDomain.toLowerCase()) ? v : '';
  }

  // == crews (multi-crew phase C) ============================================

  async myCrews(): Promise<CrewMembership[]> {
    const { data, error } = await this.sb.rpc('my_crews');
    if (error) throw new RepoError('INTERNAL', error.message);
    return ((data ?? []) as MyCrewsRow[]).map((r) => ({
      crewId: r.crew_id,
      slug: r.slug,
      name: r.name,
      role: r.role === 'owner' ? 'owner' : 'player',
      memberCount: r.member_count ?? 0,
    }));
  }

  /**
   * The server-side switch (D2). `crewId` is repointed here too, because every
   * write below stamps it directly and would otherwise keep addressing the
   * crew we just left.
   *
   * The caller drains in-flight writes FIRST (`whenIdle`, D10) — doing it here
   * would be too late: a commit already open has its own `my_crew_id()` to
   * resolve, on the server, after this returns.
   */
  async setActiveCrew(crewId: string): Promise<void> {
    const { error } = await this.sb.rpc('set_active_crew', { p_crew_id: crewId });
    if (error) throw new RepoError('INTERNAL', error.message);
    this.crewId = crewId;
  }

  async createCrew(input: CreateCrewInput): Promise<CrewMembership> {
    const acc = input.account;
    const res = await this.invoke<{
      crew_id: string;
      slug: string;
      name: string;
      sign_in_as: string | null;
    }>('crew-create', {
      crew_name: input.name,
      ...(acc
        ? { display_name: acc.displayName, email: acc.email, password: acc.password }
        : {}),
    });
    if (!res.ok) throw new RepoError(res.code, res.message);

    // Signed-out arm: the function deliberately returns no session (D6), so the
    // account it just made cannot do anything until we sign in as it. Doing it
    // here rather than in the caller keeps "created" and "usable" from being
    // two states the UI has to reason about.
    if (acc && res.data.sign_in_as) {
      await this.signIn(res.data.sign_in_as, acc.password);
    }

    // The function already switched the server-side active crew inside its
    // transaction, so the local pointer follows without a second round trip.
    this.crewId = res.data.crew_id;
    return {
      crewId: res.data.crew_id,
      slug: res.data.slug,
      name: res.data.name,
      role: 'owner',
      memberCount: 1,
    };
  }

  async deleteCrew(crewId: string, confirmName: string): Promise<void> {
    const res = await this.invoke('crew-delete', {
      crew_id: crewId,
      confirm_name: confirmName,
    });
    if (!res.ok) throw new RepoError(res.code, res.message);
    // Deliberately NOT repointing `crewId` here: which crew the account lands
    // on next is `my_crew_id()`'s answer, not ours to guess, and the store
    // re-boots through `myCrews()` after a delete.
  }

  private toPlaytest(
    p: PlaytestRow,
    int: Record<string, number>,
    st: Record<string, PlaytestStatus>,
  ): Playtest {
    return {
      id: p.id,
      kind: p.kind === 'off' ? 'off' : 'steam',
      appid: p.appid ?? 0,
      artUrl: p.art_url ?? '',
      name: p.name,
      dev: p.dev,
      hue: p.hue,
      party: { mx: p.party_max, lbl: p.party_label, vs: p.party_vs },
      status: p.status === 'closed' ? 'closed' : p.status === 'none' ? 'none' : 'open',
      ended: p.ended,
      wStart: p.window_start ? Date.parse(p.window_start) : 0,
      wEnd: p.window_end ? Date.parse(p.window_end) : 0,
      foot: p.foot ?? '',
      auto: p.auto,
      // Auto-tracked rows have created_by null, matching the prototype's by: ''.
      by: (p.created_by && this.keyById.get(p.created_by)) || '',
      at: Date.parse(p.created_at),
      note: p.note ?? '',
      desc: p.description ?? '',
      url: p.url ?? '',
      srcLabel: p.src_label ?? '',
      datesSrc: p.dates_src ?? '',
      int: { ...int },
      st: { ...st },
    };
  }

  private toGame(r: VGameRow): Game {
    const appid = r.appid ?? 0;
    // `discover_page` / `games_bootstrap` decorate the v_games shape with
    // Steam-display keys the generated view type cannot know about. All five
    // are optional ON THE WIRE, not just in the type: an RPC deploy older than
    // the migration that added each one (2901 for the first three, 2905 for
    // `pop_score`, 2907 for `players_unbounded`) omits the key entirely — so
    // absent stays absent rather than becoming `[]`, `''`, `0` or `false`,
    // matching how `owned`/`why` are handled by the caller. (Every read comes
    // through the RPCs now — `fetchOneGame` included — but a stale deploy is a
    // state this mapper still has to serve.)
    //
    // `players_unbounded` differs from the other four in kind — it is a real
    // `v_games` column rather than an RPC decoration — but the checked-in
    // generated types predate 2907, so it reaches the mapper through the same
    // widening until they are regenerated.
    const x = r as VGameRow & {
      steam_tags?: unknown;
      player_cat_ids?: unknown;
      ccu_at?: unknown;
      pop_score?: unknown;
      players_unbounded?: unknown;
      // A real `v_games` column since migration 3040, reaching the mapper
      // through this widening for the same reason `players_unbounded` does:
      // the checked-in generated types predate it.
      review_score?: unknown;
    };
    const g: Game = {
      id: appid,
      appid,
      n: r.name ?? 'Steam App #' + appid,
      dev: r.dev ?? '—',
      rd: r.release_ym ?? '',
      p: r.price === null || r.price === undefined ? null : Number(r.price),
      o: r.old_price === null || r.old_price === undefined ? 0 : Number(r.old_price),
      // 0, not 1, on a NULL. 0 is this client's "nobody told us" sentinel:
      // `knownOf` reports the player count unknown, so `passFlt` never excludes
      // on it (DISCOVERY decision 5) and `playersTxt` falls back to the
      // category label or an em dash. 1 is a positive SOLO claim no NULL
      // supports — and `mx: 1` fails every crew lens of 2+, so the row would
      // quietly vanish from Discover rather than render honestly.
      mn: r.min_players ?? 0,
      mx: r.max_players ?? 0,
      plat: r.platforms ?? 'P',
      tags: r.tags ?? [],
      rp: r.review_pct ?? 0,
      pk: r.peak_ccu ?? 0,
      ccu: r.live_ccu ?? 0,
      up: r.upcoming ?? false,
      ds: r.description ?? '',
      ea: r.early_access ?? false,
      demo: r.has_demo ?? false,
      dAid: r.demo_appid ?? 0,
      art: r.art_url ?? '',
      live: false,
      thumb: '',
      prov: r.is_curated === false,
    };
    // Steam's review rung (3040). Absent stays absent — a deploy that predates
    // the migration omits the key, and 0 would be a claim rather than a
    // silence, exactly as for the four decorations above.
    if (typeof x.review_score === 'number' && x.review_score >= 1 && x.review_score <= 9) {
      g.rs = x.review_score;
    }
    if (Array.isArray(x.steam_tags)) {
      g.stags = x.steam_tags.filter((t): t is string => typeof t === 'string');
    }
    if (Array.isArray(x.player_cat_ids)) {
      g.pcats = x.player_cat_ids.filter((c): c is number => typeof c === 'number');
    }
    // The live_ccu sample stamp (2901). Absent-safe for the same deploy-skew
    // reason as the two above; `detailVM` reads it as the freshness gate on
    // the PLAYING NOW line, so a missing stamp simply means "no live claim".
    if (typeof x.ccu_at === 'string' && x.ccu_at !== '') {
      g.ccuAt = x.ccu_at;
    }
    // The weekly popularity score (2904's `refresh_pop_scores`, emitted per row
    // by 2905). ABSENT AND `0` ARE DIFFERENT ANSWERS and this branch exists to
    // keep them apart: the server writes NULL when it has no sample inside the
    // 7-day window and a real number — which can legitimately be 0 — when it
    // does, so a `?? 0` here would turn "never sampled" into the claim "nobody
    // is playing". That is the opposite of the `mn`/`mx` treatment above, and
    // deliberately: there, 0 IS the unknown sentinel; here it is data.
    //
    // Both serializers build the row as jsonb, so a `numeric` column crosses
    // the wire as a JSON number. The string arm is for the transport, not the
    // value: a plain PostgREST select of a `numeric` arrives as a string, and a
    // future read that takes that route should not silently lose the field.
    // (An empty string is NOT a zero, whatever `Number('')` says, so the string
    // arm requires content before it converts.)
    const rawPop = x.pop_score;
    const pop =
      typeof rawPop === 'number'
        ? rawPop
        : typeof rawPop === 'string' && rawPop.trim() !== ''
          ? Number(rawPop)
          : NaN;
    if (Number.isFinite(pop)) g.popScore = pop;
    // The banding flag (2907: `game_players.res_unbounded`, surfaced by
    // `v_games` and carried by both serializers). Set ONLY on a literal `true`,
    // the way the caller sets `owned` — here `false`, a NULL (no `game_players`
    // row, so nothing resolved) and a missing key are one answer, "render the
    // number as it stands", and absence carries all three.
    //
    // This is deliberately NOT the `pop_score` treatment above: there 0 is a
    // measurement and had to be kept apart from absence, whereas nothing on
    // this path can tell a bounded row from an unanswered one, and nothing
    // downstream would render them differently if it could. `playersTxt` is the
    // only reader — `knownOf`/`passFlt` must never see it, since a banded row
    // still has a real `mx` the crew lens has to keep matching.
    if (x.players_unbounded === true) g.punb = true;
    // Which rung resolved the count. Same absent-means-unknown treatment as
    // `punb` above, and validated against the union rather than cast to it: this
    // string comes off the wire, and a value outside the five would otherwise
    // reach a switch that has no arm for it and print nothing at all.
    const src = r.players_src;
    if (src === 'override' || src === 'editorial' || src === 'igdb' || src === 'pcgw' || src === 'steam_solo') {
      g.psrc = src;
    }
    // Steam's full release day. Cast because the column post-dates the
    // checked-in generated types (migration 3009); the next regeneration makes
    // it redundant. A `date` column arrives as `YYYY-MM-DD`, which is exactly
    // the shape `fmtDFull` parses, so there is nothing to convert.
    const rdFull = (x as { release_date?: string | null }).release_date;
    if (typeof rdFull === 'string' && rdFull !== '') g.rdFull = rdFull;
    return g;
  }

  private toEvent(e: Row<'crew_events'>): CrewEvent {
    return {
      id: String(e.id),
      kind: e.kind,
      actorKey: (e.actor_id && this.keyById.get(e.actor_id)) || '',
      subjectType: e.subject_type ?? '',
      subjectId: e.subject_id ?? '',
      payload: (e.payload as Record<string, unknown>) ?? {},
      at: Date.parse(e.created_at),
    };
  }

  /**
   * `games_bootstrap` with `p.appids = [appid]`, not a bare `v_games` read.
   *
   * The bare read answered with the raw view row, which misses everything the
   * RPC serializer layers on top: `coalesce(c.description, gf.short_desc)` and
   * the `steam_tags` / `player_cat_ids` display keys. This is the realtime
   * path — a crew-mate adds a game this session has never fetched — and the
   * upserted row must be indistinguishable from the same game arriving via
   * `fetchCatalog`, or the Detail screen renders emptier for whoever did NOT
   * add it. The RPC's needed-set + popularity head rides along uninvited;
   * only the asked-for appid is taken and the rest is dropped unmapped.
   */
  private async fetchOneGame(appid: number): Promise<Game | null> {
    const { data, error } = await this.sb.rpc('games_bootstrap', {
      p: { appids: [appid] },
    });
    if (error) return null;
    const rows = ((data as { rows?: VGameRow[] } | null)?.rows ?? []) as VGameRow[];
    const row = rows.find((r) => r.appid === appid);
    if (!row) return null;
    const g = this.toGame(row);
    this.catalog.set(g.appid, g);
    if (row.art_lib || row.art_hdr) {
      this.dbAssets.set(g.appid, { lib: row.art_lib ?? '', hdr: row.art_hdr ?? '' });
    }
    return g;
  }

  private avatarUrl(p: ProfileRow): string {
    if (!p.avatar_path) return '';
    return this.publicAvatarUrl(p.avatar_path, p.avatar_updated_at ?? p.updated_at);
  }

  /** D-10: public-bucket URLs are CDN-cached hard, so `?v=` is load-bearing. */
  private publicAvatarUrl(path: string, stamp: string): string {
    const v = Math.floor((Date.parse(stamp) || this.now()) / 1000);
    return this.env.url + '/storage/v1/object/public/avatars/' + path + '?v=' + v;
  }
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

interface PgPayload {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  new: Record<string, unknown> | null;
  old: Record<string, unknown> | null;
}

function emptyBoards(): MyBoards {
  return { potentials: [], tiers: {}, tierOrder: [], skips: [], intOrder: [], intSkips: [] };
}

function cloneBoards(b: MyBoards): MyBoards {
  return {
    potentials: [...b.potentials],
    tiers: { ...b.tiers },
    tierOrder: [...b.tierOrder],
    skips: [...b.skips],
    intOrder: [...b.intOrder],
    intSkips: [...b.intSkips],
  };
}

function emptySnapshot(): CrewSnapshot {
  return {
    // No session, so no crew was served — the same shape the no-crew state has.
    crew: null,
    members: [],
    entries: [],
    playtests: [],
    seen: [],
    lastReviewedAt: 0,
    events: [],
    videos: [],
  };
}

/**
 * A raw `votes.value` column → the call it means; `null` reads as no call.
 *
 * Both read paths go through this one function — the boot snapshot and the
 * realtime binding — because the coercion they each carried
 * (`value === 'n' ? 'n' : 'y'`) treats every letter that is not `n` as ▲, which
 * under four calls flattens `s`, `b` and `c` into one. That is the silent
 * inversion V.md's two-step migration exists to avoid, and a second copy of the
 * map in this file is how it would come back on one of the two sites.
 *
 * The letters themselves are `sanitizeVotes`' business, not this file's: the
 * client keeps exactly one definition of what an old value is worth (V.md
 * decision 2), which it needs permanently — the database answers in both
 * alphabets between migrations 3000 and 3001, and a tab still running the old
 * bundle writes `y` for as long as it stays open.
 */
function normalizeVote(value: string | null): VoteValue | null {
  return sanitizeVotes({ v: value ?? '' })['v'] ?? null;
}

/** Narrow a `goty_entries.tier` column value to a `Tier`; `null` when unknown. */
function asTier(v: string | null): Tier | null {
  return v !== null && (TIERS as readonly string[]).includes(v) ? (v as Tier) : null;
}

/** `tierOrder` is a flat S→A→B→C→D→E→F→X concatenation; split it into rows (§1.6). */
function tierSequences(b: MyBoards): Record<Tier, GameId[]> {
  const out = Object.fromEntries(TIERS.map((t) => [t, [] as GameId[]])) as Record<Tier, GameId[]>;
  for (const id of b.tierOrder) {
    const t = b.tiers[String(id)];
    if (t) out[t].push(id);
  }
  return out;
}

function sameSeq(a: readonly GameId[], b: readonly GameId[]): boolean {
  return a.length === b.length && a.every((x, i) => String(x) === String(b[i]));
}

/**
 * The single element whose position changed between two orders, or null when
 * the change is not a one-item move. Covers the three shapes the UI produces:
 * a reorder, an append of a brand-new id, and a no-op.
 */
export function singleMove(prev: readonly number[], next: readonly number[]): number | null {
  const prevSet = new Set(prev);
  const added = next.filter((n) => !prevSet.has(n));
  if (added.length > 1) return null;
  if (added.length === 1) {
    const rest = next.filter((n) => n !== added[0]);
    return sameSeq(rest, prev.filter((n) => next.includes(n))) ? (added[0] ?? null) : null;
  }
  if (sameSeq(prev, next)) return null;
  // Same membership, different order: find the one id that, removed from both,
  // leaves identical sequences.
  for (const cand of next) {
    const a = prev.filter((n) => n !== cand);
    const b = next.filter((n) => n !== cand);
    if (sameSeq(a, b)) return cand;
  }
  return null;
}

/**
 * D-10 / ARCHITECTURE §1.10: the prototype's `onPickAv` centre-crop, bumped
 * 128 → 256 px. Supabase image transformations are a paid-plan feature, so this
 * canvas pass IS the normalizer — and it also keeps the upload well under the
 * bucket's 512 KB limit. Outside a DOM (tests, Node) the bytes pass through.
 */
export async function normalizeAvatar(dataUrl: string): Promise<Blob> {
  const raw = await dataUrlToBlob(dataUrl);
  if (typeof document === 'undefined' || typeof Image === 'undefined') return raw;
  try {
    const img = await loadImage(dataUrl);
    const canvas = document.createElement('canvas');
    canvas.width = AVATAR_PX;
    canvas.height = AVATAR_PX;
    const ctx = canvas.getContext('2d');
    if (!ctx) return raw;
    const side = Math.min(img.width, img.height);
    ctx.drawImage(
      img,
      (img.width - side) / 2,
      (img.height - side) / 2,
      side,
      side,
      0,
      0,
      AVATAR_PX,
      AVATAR_PX,
    );
    const out = await new Promise<Blob | null>((res) =>
      canvas.toBlob((b) => res(b), 'image/jpeg', 0.9),
    );
    return out ?? raw;
  } catch {
    return raw;
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = () => rej(new Error('IMAGE_DECODE'));
    img.src = src;
  });
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const m = /^data:([^;,]+)?(;base64)?,(.*)$/s.exec(dataUrl);
  if (!m) throw new RepoError('BAD_IMAGE', 'Not a data URL');
  const type = m[1] || 'image/jpeg';
  const body = m[3] ?? '';
  if (m[2]) {
    const bin = atob(body);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type });
  }
  return new Blob([decodeURIComponent(body)], { type });
}
